/* asset refresh-c52d58e-2026072001 */
/*
  MONO Configurator — v4

  Highlights of this revision
  ---------------------------
  - Floor mesh is gone. The scene is solid white; the only thing
    grounding the object is a soft canvas-textured shadow blob.
  - Camera auto-fits to the stack height on init / reset / module
    add-remove so the full silhouette is always visible.
  - Active-slot state collapses every card except the focused one
    (accordion behaviour) so the panel never needs an inner scroll.
  - Glow finish + dark mode now drives emissive on the inner cup +
    a softer emissive on the shell, so the modules visibly shine.
  - Compact icon-based Matte / Glow toggle. Shape list trimmed to
    three entries (Lantern + two upcoming).
*/

import * as THREE        from 'three';
import { OrbitControls }  from 'three/addons/controls/OrbitControls.js';
import { GLTFLoader }     from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader }    from 'three/addons/loaders/DRACOLoader.js';
import { EffectComposer }  from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass }      from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutlinePass }     from 'three/addons/postprocessing/OutlinePass.js';
import { OutputPass }      from 'three/addons/postprocessing/OutputPass.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

// `window.LaseConfiguratorConfig` is populated by the non-module
// assets/configurator-config.js script, loaded by sections/configurator.liquid
// BEFORE this module. Using a relative ES import would bypass Shopify's
// asset_url cache-busting query and serve a stale copy after every
// catalogue edit — using the global keeps the version param honest.
const LaseConfiguratorConfig = (typeof window !== 'undefined' && window.LaseConfiguratorConfig) || {};

// All part definitions (sizes, finishes, colours, SKUs, materials)
// live in ./configurator-config.js — a single source of truth that the user
// edits to update the catalogue. See MONO-BACKEND.md for the schema.
const DEFAULT_CONFIG = LaseConfiguratorConfig;

const STORAGE_KEY_THEME = 'lase-theme';

class MonoConfigurator {
  constructor (root, config) {
    this.root      = root;
    this.config    = config;
    this.canvas    = root.querySelector('[data-cfg-canvas]');
    this.slotList  = root.querySelector('[data-cfg-slots]');
    this.priceEl   = root.querySelector('[data-cfg-price]');
    this.modeList  = root.querySelector('[data-cfg-mode]');
    this.dimsRoot  = root.querySelector('[data-cfg-dims]');
    this.dimsOverlay = root.querySelector('[data-cfg-dims-overlay]');

    this.modules = Array.from({ length: config.defaultModuleCount }, () => ({
      sizeId:   config.defaultSize,
      shapeId:  config.defaultShape,
      finishId: config.defaultFinish,
      colorIdx: config.defaultModuleColorIdx
    }));
    this.connectors = Array.from({ length: config.defaultModuleCount - 1 }, () => ({
      colorIdx: config.defaultConnectorColorIdx
    }));
    this.lightAddonId = 'none';
    this.viewMode     = 'compact';
    this.fullscreen   = false;
    // "Use as" flags — future add-on modules (light kit / vase
    // insert), both on by default. Must exist BEFORE the first
    // renderStack so the build chips include them.
    this.useAs = { light: true, vase: true };
    // Dimension koty in 3D are off by default — toggled by the
    // ruler icon in the bottom-right cluster.
    this.showDimsOverlay = false;
    // No card open on initial load — explicit click required (in 3D or
    // on a card header). Click on empty space clears it again.
    this.activeSlot   = { kind: null, idx: null };

    this.gltfLoader = new GLTFLoader();
    const draco = new DRACOLoader();
    draco.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
    this.gltfLoader.setDRACOLoader(draco);

    this.modelCache = new Map();
    this._sortable  = null;

    // Did we open with a specific configuration? (a shared/preset hash
    // or a `from=product` deep-link from a PDP "Customize" button). If
    // NOT, the editor opens EMPTY with the "how do you want to start"
    // guide rendered in the panel's slot column instead of module cards.
    this._openedWithState =
      (location.hash || '').startsWith('#c=') ||
      new URLSearchParams(location.search).has('from');
    this.showGuide = !this._openedWithState;
    if (this.showGuide) {
      this.modules = [];
      this.connectors = [];
    }

    // Load shared state from the URL hash BEFORE the first paint, so
    // a copy-pasted "shareable" link opens with the same modules /
    // colours / view mode / light addon the original user picked.
    // See encodeState() / decodeState() for the URL format. Falls
    // through silently when the hash is absent or malformed.
    this.loadStateFromUrl();

    this.initThree();
    this.buildUI();
    this.rebuildScene().then(() => this.fitCameraToStack());
    this.tickPrice();
    this.renderDimensionsPanel();
    this.observeTheme();

    // Push the initial state to the hash so the URL is always
    // shareable even before the user touches anything. (Skipped while
    // the start guide shows — an empty hash would make a plain reload
    // look like a deep-link and hide the guide.)
    this.syncStateToUrl();
  }

  // ---- Start guide (in-panel: design / scratch / random) --------
  getPresets () {
    if (this._presets) return this._presets;
    let presets = [];
    const tag = this.root.querySelector('[data-cfg-presets]');
    if (tag) {
      // Tolerate a trailing comma from the Liquid loop.
      try { presets = JSON.parse(tag.textContent.replace(/,\s*]/g, ']')); }
      catch (e) { presets = []; }
    }
    presets = presets.filter(p => p && p.hash && p.img);
    // De-dup by hash (a design collection may repeat the same recipe).
    const seen = new Set();
    this._presets = presets.filter(p => (seen.has(p.hash) ? false : seen.add(p.hash)));
    return this._presets;
  }

  // Rendered INSTEAD of the module cards when the configurator opens
  // fresh (no preset hash / deep-link). Three ways in: pick a design
  // (carousel, expandable to a full grid), start from scratch, or
  // randomize. Picking any of them dismisses the guide and the normal
  // cards take over.
  renderGuide () {
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const presets = this.getPresets();
    const diceSvg = `
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="3"/>
        <circle cx="8" cy="8" r="1.4" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="8" r="1.4" fill="currentColor" stroke="none"/>
        <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/>
        <circle cx="8" cy="16" r="1.4" fill="currentColor" stroke="none"/>
        <circle cx="16" cy="16" r="1.4" fill="currentColor" stroke="none"/>
      </svg>`;

    // Infinite design slider: the thumbnail set renders 3× and the
    // scroll position wraps between the copies, so the row repeats
    // seamlessly in both directions — no "view all" needed.
    const designButtons = presets.map((p, i) => `
      <button type="button" class="cfg-guide__design" data-guide-preset="${i}" aria-label="${esc(p.title || 'Design')}">
        <img src="${esc(p.img)}" alt="" loading="lazy">
      </button>`).join('');
    const designSection = presets.length ? `
      <div class="cfg-guide__section">
        <p class="cfg-guide__title">Start from a design</p>
        <p class="cfg-guide__hint">Pick one to make it yours — colours, sizes, order.</p>
        <div class="cfg-guide__slider">
          <button type="button" class="cfg-guide__nav" data-guide-nav="-1" aria-label="Previous designs">‹</button>
          <div class="cfg-guide__designs" data-guide-designs>
            ${designButtons}${designButtons}${designButtons}
          </div>
          <button type="button" class="cfg-guide__nav" data-guide-nav="1" aria-label="More designs">›</button>
        </div>
      </div>` : '';

    this.slotList.innerHTML = `
      <div class="cfg-guide">
        ${designSection}
        <div class="cfg-guide__section cfg-guide__section--cta">
          <p class="cfg-guide__title">Start from scratch</p>
          <p class="cfg-guide__hint">An empty canvas — add modules one by one.</p>
          <button type="button" class="cfg-guide__btn" data-guide-scratch>Start from scratch →</button>
        </div>
        <div class="cfg-guide__section cfg-guide__section--cta">
          <p class="cfg-guide__title">Feeling lucky?</p>
          <p class="cfg-guide__hint">We roll a lamp for you — reroll as often as you like.</p>
          <button type="button" class="cfg-guide__btn cfg-guide__btn--random" data-guide-random>${diceSvg} Random</button>
        </div>
      </div>`;

    const designs = this.slotList.querySelector('[data-guide-designs]');
    if (designs) {
      designs.addEventListener('click', (e) => {
        const b = e.target.closest('[data-guide-preset]');
        if (!b) return;
        const p = presets[+b.dataset.guidePreset];
        if (p) this.applyPreset(p.hash);
      });
      // Start in the middle copy and wrap the scroll position whenever
      // it drifts into the outer copies — the content is identical, so
      // the jump is invisible and the slider loops forever.
      requestAnimationFrame(() => {
        const setW = designs.scrollWidth / 3;
        designs.scrollLeft = setW;
        designs.addEventListener('scroll', () => {
          const w = designs.scrollWidth / 3;
          if (designs.scrollLeft < w * 0.5)      designs.scrollLeft += w;
          else if (designs.scrollLeft > w * 1.5) designs.scrollLeft -= w;
        });
      });
      // Chevron paging — one "page" ≈ the visible row width. Native
      // smooth scrolling stalls next to the WebGL rAF loop, so we
      // animate by hand. Before each page the position normalises deep
      // into the middle copy so the loop-wrap can't fire mid-animation.
      const pageBy = (dir) => {
        const w = designs.scrollWidth / 3;
        if (designs.scrollLeft < w * 0.75)      designs.scrollLeft += w;
        else if (designs.scrollLeft > w * 1.25) designs.scrollLeft -= w;
        const start = designs.scrollLeft;
        const delta = dir * designs.clientWidth * 0.8;
        const t0 = performance.now();
        const D = 320;
        const step = (t) => {
          const k = Math.min((t - t0) / D, 1);
          const e = 1 - Math.pow(1 - k, 3);   // easeOutCubic
          designs.scrollLeft = start + delta * e;
          if (k < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      };
      this.slotList.querySelectorAll('[data-guide-nav]').forEach(btn => {
        btn.addEventListener('click', () => pageBy(+btn.dataset.guideNav));
      });
    }
    const scratch = this.slotList.querySelector('[data-guide-scratch]');
    if (scratch) scratch.addEventListener('click', () => this.startFromScratch());
    const random = this.slotList.querySelector('[data-guide-random]');
    if (random) random.addEventListener('click', () => { this.dismissGuide(); this.randomize(); });
  }

  dismissGuide () {
    if (!this.showGuide) return;
    this.showGuide = false;
    this.root.classList.remove('cfg--guide');
  }

  applyPreset (hash) {
    const state = this.decodeState(hash);
    this.dismissGuide();
    if (state) {
      this.applyState(state);
      this._popAll = true;
      this.renderStack();
      this.renderLightSection();
      this.rebuildScene().then(() => this.fitCameraToStack());
      this.tickPrice();
      this.renderDimensionsPanel();
      this.syncStateToUrl();
    }
  }

  startFromScratch () {
    this.dismissGuide();
    this.modules = [];
    this.connectors = [];
    this.activeSlot = { kind: null, idx: null };
    this.renderStack();
    this.renderLightSection();
    this.rebuildScene();
    this.tickPrice();
    this.renderDimensionsPanel();
    this.syncStateToUrl();
  }

  // ============== Theme ==============

  // Viewport-only dark mode: flips the 3D preview (backdrop, glow
  // emissives) and NOTHING else — panel, header and cards stay light.
  // The old global html.is-dark theme is retired.
  isDark () {
    return !!this.viewDark;
  }

  observeTheme () {
    // A fresh open (start guide showing) always begins in DAY — a
    // persisted night preference over an empty black viewport reads
    // as broken. Deep-links / shared configs keep the remembered mode.
    if (this.showGuide) {
      this.viewDark = false;
    } else {
      try { this.viewDark = localStorage.getItem('lase-view-dark') === '1'; }
      catch (e) { this.viewDark = false; }
    }
    this.applyTheme();
  }

  applyTheme () {
    if (!this.scene) return;
    const dark = this.isDark();
    // Drives the in-viewport day/night pill state (puck position, icons).
    if (this.root) this.root.classList.toggle('cfg--night', dark);

    // Day: TRANSPARENT — the page white shows through the canvas, same
    // as the homepage preview (and immune to tone-mapping tint).
    // Night: solid deep charcoal.
    this.scene.background = dark ? this._nightBg : null;

    // Same tone mapping as the homepage preview in day (Neutral @1.05);
    // night lifts exposure a touch for the glow. Materials are
    // recreated right after by rebuildScene(), so the program change
    // is picked up.
    if (this.renderer) {
      this.renderer.toneMapping = THREE.NeutralToneMapping ?? THREE.LinearToneMapping;
      this.renderer.toneMappingExposure = dark ? 1.15 : 1.05;
    }

    // Environment intensity — day is the homepage preview's rig dialled
    // to 90% (user: full brightness read as overexposed); night drops
    // so the studio cues vanish.
    this.scene.environmentIntensity = dark ? 0.3 : 0.9;

    // Direct lights — day = homepage preview rig × 0.9.
    // Night: dimmer, and rim kept low so white highlights on matte
    // shells never cross the bloom threshold.
    if (this._key)  this._key.intensity  = dark ? 0.45 : 1.40;
    if (this._fill) this._fill.intensity = dark ? 0.16 : 0.41;
    if (this._rim)  this._rim.intensity  = dark ? 0.4  : 0.59;
    if (this._hemi) this._hemi.intensity = dark ? 0.06 : 0.16;


    // Ground decoration:
    //   Day  — wide, fully opaque dark blob. The lamp really plants
    //          on the surface — a strong dark contact ring radiating
    //          out under the base.
    //   Night — same texture at scale 0.8 + full opacity. Reads as
    //          a punchy dark patch against the washed charcoal bg
    //          even when no Glow halo is present.
    //   The warm glow halo (refreshGlowFloor) only paints when the
    //   bottom module is a Glow finish; otherwise we see just the
    //   shadow against the washed dark backdrop.
    if (this.shadowMesh) {
      // Day: tight + dark right under the base so the lamp sits
      // planted on the surface — but the feathered texture keeps the
      // edge soft. Night: faint wide wash.
      this.shadowMesh.material.opacity = dark ? 0.3 : 0.75;
      const s = dark ? 1.0 : 0.5;
      this.shadowMesh.scale.set(s, s, 1);
    }
    this.refreshGlowFloor();

    // Bloom — only used at night, and even then much subtler than
    // before. User feedback: bloom was painting a flashlight-bright
    // halo where they wanted a tea-light wisp. Strength 0.50 → 0.10.
    //
    // Light mode bloom strength clamped to 0 — the user asked for no
    // bloom during the day at all. The 0.6 emissive on the EXT shell
    // still reads as a slight "luminous tint" without any halo, and
    // the per-module PointLight (addGlowHalo) does the actual
    // neighbouring-illumination work in day mode.
    if (this.bloomPass) {
      // The additive halo sprite (makeGlowSprite) now carries the
      // visible glow. Bloom is dialled WAY back — only adds a
      // touch of softness where the sprite's centre saturates the
      // framebuffer; the bulk of the halo is the sprite itself.
      // Threshold stays at 1.0 so the bloom pass doesn't fuzz the
      // rest of the scene.
      // Dark mode: the glow modules' emissive surfaces are pushed above
      // this threshold (see applyModuleColor), so bloom blooms the LAMP
      // itself into a soft luminous halo — the light emanates from the
      // object, not a backdrop sprite. Day: no bloom (clean product shot).
      // Gentle, tight bloom that hugs the lit surface — not a wide haze
      // cloud. Strength/radius pulled well down from pass 2.
      // Bloom only exists at night; in day the pass is fully DISABLED
      // (needed for the transparent background, and free performance).
      // Threshold sits just under the luma-normalised emissive target
      // (see applyModuleColor) so EVERY colour blooms equally — the
      // normalisation is what makes the bloom "spectrum-dynamic":
      // red gets a higher emissive multiplier, yellow a lower one,
      // both land on the same post-multiply luma.
      this.bloomPass.enabled   = dark;
      this.bloomPass.strength  = dark ? 0.22 : 0.0;
      this.bloomPass.radius    = dark ? 0.5  : 0.0;
      this.bloomPass.threshold = dark ? 0.22 : 1.0;
    }
    // Outline reads against both backdrops by picking contrast hex.
    if (this.outlinePass) {
      // Outline colour per mode.
      //
      // OutlinePass composites via essentially-additive blending:
      // edge_color × edge_mask is layered onto the rendered scene.
      // Black (0,0,0) therefore contributes nothing visible — it
      // multiplies to zero regardless of edge mask strength. This
      // is why our earlier attempt at "pure black for light mode"
      // produced no visible outline at all.
      //
      // For visibility we need a SATURATED colour with non-zero
      // RGB. We also crank edgeStrength higher in light mode so
      // the contribution wins over the bright off-white backdrop.
      //   dark  → gold #c9a449 + standard strength
      //   light → vivid red #d32f2f + boosted strength
      const outlineHex = dark ? '#c9a449' : '#d32f2f';
      this.outlinePass.visibleEdgeColor.set(outlineHex);
      this.outlinePass.hiddenEdgeColor.set(outlineHex);
      this.outlinePass.edgeStrength = dark ? 3.0 : 3.5;
    }
    // Repaint emissives + ground decoration for new mode
    this.rebuildScene();
  }

  // ============== Three.js setup ==============

  initThree () {
    const { width: w, height: h } = this.canvasSize();

    // Perf tier — coarse pointer (touch) or a small viewport means a
    // phone/tablet; we trim the heaviest effects (transmission,
    // shadow-map size) on those so the scene stays smooth.
    this.lowPerf = (window.matchMedia && window.matchMedia('(pointer: coarse)').matches)
                || Math.min(window.innerWidth, window.innerHeight) < 560;

    // alpha:true — in day mode the scene background is null and the
    // page's own white (--cfg-bg) shows through the canvas, exactly
    // like the homepage random-builds preview. That sidesteps the
    // tone-mapping-tints-white problem entirely.
    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
    this.renderer.setClearAlpha(0);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.setSize(w, h, false);
    // KhronosPBRNeutral tone mapping — purpose-built for commerce product
    // viewers: filmic highlight rolloff (no blown-out plastic sheen) while
    // preserving saturated brand hues, which is exactly the fight the old
    // Linear/ACES workaround was losing (ACES shifted orange→brown; Linear
    // was flat). Falls back to Linear on older three builds.
    this.renderer.toneMapping = THREE.NeutralToneMapping ?? THREE.LinearToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    // Real shadow maps were tried but read too hard/present — the user
    // wants a soft, blurred ground shadow. We use a soft canvas-painted
    // radial blob (makeContactShadow) instead; no shadow-map cost.
    this.renderer.shadowMap.enabled = false;
    // Enable per-material clippingPlanes — used by the glow halo
    // sprite (makeGlowSprite) to cut off pixels below world y=0
    // so the halo never crosses under the floor.
    this.renderer.localClippingEnabled = true;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    // Real shadow maps were tried but didn't read well across setups
    // — we now use a canvas-painted contact AO blob (day) + a warm
    // glow halo (night) as ground decoration instead. See
    // makeContactShadow + makeGlowFloor below.

    this.scene = new THREE.Scene();
    // Day: TRANSPARENT background (page white shows through) — same as
    // the homepage random-builds preview. Night: solid deep charcoal.
    // Swapped per theme in applyTheme.
    this.scene.background = null;
    // Neutral warm-black night backdrop — user-picked #0f0e0e (the
    // lifted charcoals read too blue).
    this._nightBg = new THREE.Color(0x0f0e0e);

    // RoomEnvironment for image-based lighting — the SAME env the
    // homepage random-builds preview uses, so both viewers read the
    // colours identically (the custom studio box rendered them darker).
    // envMapIntensity per material is set in cloneModel; the global
    // multiplier scene.environmentIntensity is driven by applyTheme.
    this._pmrem = new THREE.PMREMGenerator(this.renderer);
    this.scene.environment = this._pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 1.0;
    this._pmrem.dispose();

    this.camera = new THREE.PerspectiveCamera(32, w / h, 0.05, 50);
    this.raycaster = new THREE.Raycaster();
    // We want the model to render LEFT of viewport centre (away from
    // the right-side floating panel) WITHOUT shifting the orbit pivot
    // off the model — otherwise the lamp visibly swings on a circle
    // around an off-axis point as the camera auto-rotates.
    //
    // Solution: camera + target both sit on the model's centre line
    // (X = 0). The off-centre framing is produced via
    // `camera.setViewOffset(...)` (Three.js's asymmetric-frustum
    // API), which shifts the rendered viewport without moving the
    // optical pivot.  The pan is only useful when the panel is
    // taking up the right-side real estate — fullscreen + mobile
    // layouts give the canvas the whole width, so cameraPanX
    // collapses to 0 there. recomputeCameraPan() handles the logic.
    this.cameraPanX = 0;
    this.camera.position.set(0.6, 0.32, 0.95);

    this.controls = new OrbitControls(this.camera, this.canvas);
    this.controls.enableDamping  = true;
    this.controls.dampingFactor  = 0.08;
    this.controls.target.set(0, 0.2, 0);
    // FIXED camera: no user zoom / rotation / pan — the lamp centres
    // itself (fitCameraToStack on every stack edit) and turns gently
    // on its own, forever. The viewport is a stage, not a camera rig.
    this.controls.enableRotate = false;
    this.controls.enableZoom   = false;
    this.controls.enablePan    = false;
    // Fully disable input handling so touches pass through to the page
    // (mobile scroll). update() still drives autoRotate — the enabled
    // flag only gates the event handlers.
    this.controls.enabled = false;
    this.controls.autoRotate = true;
    this.controls.autoRotateSpeed = 0.55;
    this.recomputeCameraPan();

    // Hemisphere light stays in WORLD space — it represents an
    // omnidirectional sky/ground tint that shouldn't follow the
    // viewer. Keeps a faint sense of "up vs down" baked into the scene.
    this._hemi = new THREE.HemisphereLight(0xffffff, 0xeeeae0, 0.18);
    this.scene.add(this._hemi);

    // ---- Camera-parented 3-point rig ------------------------------
    // The key/fill/rim are attached to the camera so the lit side of
    // the lamp always faces the viewer. Previously they were world-
    // anchored — physically realistic but the model visibly darkened
    // on the way around because different sides took the shadow.
    // The env map (PMREM'd RoomEnvironment) stays in world space, so
    // reflections still rotate as the camera orbits and we keep the
    // sense of 3D depth without the brightness wobble.
    //
    // Positions below are RELATIVE TO THE CAMERA — small offsets,
    // pointing toward the camera's forward direction (-Z in local
    // camera space).
    this.scene.add(this.camera);   // camera needs to be in the graph
                                   // for children's matrices to update

    // Neutral white — a warm key tinted every colour amber-ward.
    this._key = new THREE.DirectionalLight(0xffffff, 1.55);
    this._key.position.set(0.6, 0.85, 0.4);          // upper-right of camera
    this._key.target.position.set(0, -0.1, -1);      // pointing forward
    this.camera.add(this._key);
    this.camera.add(this._key.target);

    this._fill = new THREE.DirectionalLight(0xffffff, 0.45);
    this._fill.position.set(-0.9, 0.45, 0.25);       // upper-left of camera
    this._fill.target.position.set(0, 0, -1);
    this.camera.add(this._fill);
    this.camera.add(this._fill.target);

    // Backlit rim — pulls the silhouette in night mode against the
    // dark backdrop. Mounted "behind the camera" pointing toward the
    // subject so it grazes the far side of the lamp.
    this._rim = new THREE.DirectionalLight(0xffffff, 0.65);
    this._rim.position.set(0, 0.7, 0.7);             // slightly up, behind
    this._rim.target.position.set(0, 0, -1);
    this.camera.add(this._rim);
    this.camera.add(this._rim.target);

    // Ground decoration layers — two canvas-baked planes that swap
    // visibility per theme:
    //   • shadowMesh  — dark radial AO. Big + strong in day so the
    //     lamp clearly stands; small + nearly-black in night so it
    //     still grounds against the washed backdrop.
    //   • glowFloor   — warm radial halo with additive blending. Only
    //     visible in night, simulates the lamp illuminating the
    //     floor underneath it. Tinted to the bottom module's colour
    //     when that module is set to the Glow finish.
    // Soft blurred contact shadow — a wide, low-opacity radial blob
    // that gently grounds the lamp without a hard edge.
    this.shadowMesh = this.makeContactShadow();
    this.scene.add(this.shadowMesh);
    this.glowFloor = this.makeGlowFloor();
    this.scene.add(this.glowFloor);

    this.partsGroup = new THREE.Group();
    // Model stays anchored at world origin; only the camera + target
    // shift right (see `targetXShift` above), which makes the model
    // appear LEFT of viewport centre — away from the right-side panel.
    this.partsGroup.position.x = 0;
    this.scene.add(this.partsGroup);

    // Post-processing pipeline: render → bloom (glow halo) → outline
    // (selected module rim) → output. Bloom strength driven by
    // applyTheme(); outline targets driven by refreshOutline().
    this.composer = new EffectComposer(this.renderer);
    this.composer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.composer.setSize(w, h);
    this.composer.addPass(new RenderPass(this.scene, this.camera));
    this.bloomPass = new UnrealBloomPass(new THREE.Vector2(w, h),
      0.0, 0.5, 0.85
    );
    this.composer.addPass(this.bloomPass);
    this.outlinePass = new OutlinePass(new THREE.Vector2(w, h), this.scene, this.camera);
    // Clean thin selection outline — NO edgeGlow. The glow was the
    // "haze/fog around the vase" the user saw when a module card was
    // open: OutlinePass painted a soft luminous halo around the
    // selected module. A crisp hairline reads as a selection, not fog.
    this.outlinePass.edgeStrength    = 3.0;
    this.outlinePass.edgeGlow        = 0.0;
    this.outlinePass.edgeThickness   = 1.0;
    this.outlinePass.downSampleRatio = 1;
    this.outlinePass.pulsePeriod     = 0;
    this.outlinePass.visibleEdgeColor.set('#c9a449');
    this.outlinePass.hiddenEdgeColor.set('#c9a449');
    this.composer.addPass(this.outlinePass);
    // (Vignette pass removed — it mixed the frame edges toward grey,
    // which read as a dirty halo in dark mode and a visible frame in
    // day mode. Clean output only.)
    this.composer.addPass(new OutputPass());

    // (Light bounce ground effect removed — kept the shadow simple.)

    // Click-to-focus raycasting
    this.bindRaycasterClick();

    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(() => this.onResize());
      this.ro.observe(this.canvas.parentElement);
    } else {
      window.addEventListener('resize', () => { this.onResize(); this.fitBuildList(); });
    }

    this.animate = this.animate.bind(this);
    this.animate();

    // Dev handle for live inspection/tuning of the render.
    try { window.__cfg = this; } catch (e) {}
  }

  bindRaycasterClick () {
    // Distinguish a tap from a drag-orbit by tracking pointer travel.
    let downX = 0, downY = 0, downT = 0;
    this.canvas.addEventListener('pointerdown', e => {
      downX = e.clientX; downY = e.clientY; downT = performance.now();
    });
    this.canvas.addEventListener('pointerup', e => {
      const dx = e.clientX - downX, dy = e.clientY - downY;
      const dt = performance.now() - downT;
      if (Math.hypot(dx, dy) > 6 || dt > 800) return; // it was a drag
      const rect = this.canvas.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      this.raycaster.setFromCamera({ x: nx, y: ny }, this.camera);
      const hits = this.raycaster.intersectObjects(this.partsGroup.children, true);
      if (!hits.length) {
        // Click on empty area clears any active selection and collapses
        // all cards. No-op if nothing was selected.
        if (this.activeSlot && this.activeSlot.kind != null) {
          this.activeSlot = { kind: null, idx: null };
          this.renderStack();
          this.refreshOutline();
        }
        return;
      }
      // Walk up parent chain to find the tagged top-level instance
      let node = hits[0].object;
      while (node && node.userData?.monoKind === undefined) node = node.parent;
      if (!node) return;
      this.focusSlot(node.userData.monoKind, node.userData.monoIdx);
    });
  }

  // `toggle = true` makes a click on an already-open slot collapse
  // it (used by the card-header path so the chevron is a true two-
  // way control). Clicks coming FROM the 3D scene call this with
  // toggle = false — clicking a part in the viewer always opens,
  // never closes, the matching card.
  focusSlot (kind, idx, toggle = false) {
    if (kind == null || idx == null) return;
    if (this.isActive(kind, idx)) {
      if (!toggle) return;
      this.activeSlot = { kind: null, idx: null };
      this.renderFloatCard();
      this.refreshOutline();
      return;
    }
    this.activeSlot = { kind, idx };
    this.dismissHintForever();
    this.renderFloatCard();
    this.refreshOutline();

    // Auto-explode to "semi" when opening a connector card from
    // compact view. Connectors are hidden in the compact silhouette
    // (modules stacked flush), so the user can't see what they're
    // editing. Semi inserts a small gap that surfaces the connector
    // without making the whole stack drift.
    if (kind === 'connector' && this.viewMode === 'compact') {
      this.setViewMode('semi');
    }
  }

  // ---- Floating in-viewport editor -------------------------------
  // The selected part's editor card floats beside the part itself,
  // tethered by a coral leader line. Content re-renders on every
  // selection / stack change; position follows the part every frame.
  renderFloatCard () {
    const float = this.root.querySelector('[data-cfg-float]');
    const host  = this.root.querySelector('[data-cfg-float-card]');
    if (!float || !host) return;
    const { kind, idx } = this.activeSlot || {};
    const valid = kind === 'module'    ? !!this.modules[idx]
                : kind === 'connector' ? !!this.connectors[idx]
                : false;
    const viewer = this.canvas?.parentElement;
    if (!valid) {
      float.setAttribute('hidden', '');
      host.innerHTML = '';
      // Mobile: give the viewer its space back when the sheet closes.
      if (viewer) viewer.style.marginBottom = '';
      this.root.classList.remove('cfg--sheet');
      this.updateHintPill();
      return;
    }
    host.innerHTML = '';
    host.appendChild(kind === 'module'
      ? this.buildModuleCard(idx)
      : this.buildConnectorCard(idx));
    float.removeAttribute('hidden');
    // Mobile sheet mode: only the vase on stage (toolbar/combi/FABs
    // hide via CSS) and the bottom bar folds back to its slim row.
    this.root.classList.add('cfg--sheet');
    const bar = this.root.querySelector('.cfg__bar');
    if (bar && window.matchMedia('(max-width: 899px)').matches) {
      bar.classList.remove('is-open');
      this.root.querySelector('[data-cfg-bar-toggle]')?.setAttribute('aria-expanded', 'false');
    }
    this.updateOverlayArrows();
    // Position after layout so offsetHeight clamping sees real size.
    requestAnimationFrame(() => {
      this.updateFloatPos();
      // Mobile: the sheet overlays the bottom bar but must NOT cover
      // the 3D view — shrink the viewer by the sheet's extra height so
      // the whole vase stays visible (the ResizeObserver re-fits the
      // camera automatically).
      if (viewer) {
        if (window.matchMedia('(max-width: 899px)').matches) {
          const extra = Math.max(0, float.offsetHeight - (bar ? bar.offsetHeight : 0));
          viewer.style.marginBottom = extra + 'px';
        } else {
          viewer.style.marginBottom = '';
        }
      }
    });
    this.updateHintPill();
  }

  // Position the floating editor beside the selected part — ONCE per
  // open / stack edit / resize, NOT per frame: the card stands still
  // while the lamp turns. Vertical anchor = the part's centre on the
  // rotation axis (static under the camera's orbit); horizontal =
  // stage centre + the part's silhouette radius in pixels + a gap.
  updateFloatPos () {
    const float = this.root.querySelector('[data-cfg-float]');
    if (!float || float.hasAttribute('hidden')) return;
    // Mobile: the editor is a bottom sheet pinned by CSS — clear any
    // inline position from a previous desktop layout and let CSS win.
    if (window.matchMedia('(max-width: 899px)').matches) {
      float.style.left = '';
      float.style.top = '';
      return;
    }
    const { kind, idx } = this.activeSlot || {};
    if (kind == null || idx == null) return;
    const mesh = this.partsGroup?.children.find(c =>
      c.userData.monoKind === kind && c.userData.monoIdx === idx
    );
    if (!mesh) return;

    const box = this.computeMeshBox(mesh, this._tmpFloatBox ||= new THREE.Box3());
    const cy = (box.min.y + box.max.y) / 2;
    // On-axis point → screen position is invariant while the camera
    // orbits the vertical axis.
    this.freshenCameraMatrices();
    const v = (this._tmpFloatVec ||= new THREE.Vector3());
    v.set(0, cy, 0).project(this.camera);
    if (v.z > 1) return;
    const rect = this.canvas.getBoundingClientRect();
    // Silhouette radius in px from the fixed camera distance + fov —
    // rotation-invariant (unlike the projected bbox).
    const dist = this.camera.position.distanceTo(this.controls.target);
    const fovRad = (this.camera.fov * Math.PI) / 180;
    const worldPerPx = (2 * dist * Math.tan(fovRad / 2)) / rect.height;
    const radiusM = ((this.dimensions().diameterMm || 95) / 2) / 1000;
    const radiusPx = radiusM / worldPerPx;

    let sx = rect.width / 2 + radiusPx + 132;  // clear gap right of the part
                                               // (leaves air for the arrow column)
    let sy = (-v.y * 0.5 + 0.5) * rect.height;
    const cardH = float.offsetHeight || 300;
    const cardW = float.offsetWidth  || 320;
    sx = Math.min(sx, rect.width - cardW - 16);
    sy = Math.min(Math.max(sy, cardH / 2 + 16), rect.height - cardH / 2 - 16);
    float.style.left = sx + 'px';
    float.style.top  = sy + 'px';
  }

  updateHintPill () {
    const pill = this.root.querySelector('[data-cfg-hint-pill]');
    if (!pill) return;
    // Per-OPENING tip: shows on every fresh load of the configurator
    // and disappears after the first module click in this instance.
    const show = !this._hintDone
      && !this.showGuide
      && this.modules.length > 0
      && this.activeSlot.kind == null;
    pill.toggleAttribute('hidden', !show);
  }

  dismissHintForever () {
    this._hintDone = true;
    const pill = this.root.querySelector('[data-cfg-hint-pill]');
    if (pill) pill.setAttribute('hidden', '');
  }

  makeContactShadow () {
    // Canvas-painted radial AO that grounds the lamp. Texture itself
    // is maxed-out black (alpha 1 in the centre, full dark region
    // out to ~0.35) so day + night both have a deep contact patch.
    // Per-theme tuning happens in applyTheme via material.opacity
    // and mesh.scale — the texture stays the same.
    const size = 256;
    const cnv = document.createElement('canvas');
    cnv.width = size; cnv.height = size;
    const ctx = cnv.getContext('2d');
    // Darker core right under the base (plants the lamp), but the long
    // feathered falloff keeps the edge blurred-soft — no hard disc.
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0,    'rgba(0,0,0,0.85)');
    g.addColorStop(0.22, 'rgba(0,0,0,0.60)');
    g.addColorStop(0.50, 'rgba(0,0,0,0.22)');
    g.addColorStop(1,    'rgba(0,0,0,0.00)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(cnv);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({
      map: tex, transparent: true, opacity: 1.0, depthWrite: false
    });
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(0.85, 0.85), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.001, 0);
    // Locks the layer order against the glow halo (renderOrder 1).
    // Without this, both floor planes sit in the transparent pass with
    // depthWrite off; Three.js sorts them by camera distance, and the
    // ~0.5 mm Y separation flips sign at certain orbit angles — the
    // dark blob occasionally rendered ON TOP of the warm halo,
    // swallowing it.
    mesh.renderOrder = 0;
    return mesh;
  }

  // Soft radial halo painted on the floor in night mode — additively
  // blended so the lamp looks like it's *lighting* the surface
  // beneath it. Texture is pure white with an alpha gradient; the
  // tint is driven by `material.color` so refreshGlowFloor can match
  // it to whichever Glow-finish colour the bottom module is wearing.
  //
  // Alpha stops are kept low: with AdditiveBlending the texture
  // alpha multiplies the *added* light, so high values blow out the
  // floor. These values feel like a candle glow, not a flashlight.
  makeGlowFloor () {
    const size = 256;
    const cnv = document.createElement('canvas');
    cnv.width = size; cnv.height = size;
    const ctx = cnv.getContext('2d');
    const g = ctx.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    g.addColorStop(0,    'rgba(255,255,255,0.32)');
    g.addColorStop(0.30, 'rgba(255,255,255,0.16)');
    g.addColorStop(0.70, 'rgba(255,255,255,0.04)');
    g.addColorStop(1,    'rgba(255,255,255,0.00)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(cnv);
    tex.colorSpace = THREE.SRGBColorSpace;
    const mat = new THREE.MeshBasicMaterial({
      map: tex,
      color: 0xffe6c2,
      opacity: 0.6,               // global multiplier on the additive amount
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false
    });
    // Slightly wider than the shadow blob so the halo extends past
    // the dark contact patch under the base.
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1.05, 1.05), mat);
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(0, 0.0015, 0); // above shadow blob to avoid z-fight
    // Glow always draws AFTER the contact shadow so its additive light
    // lands on top of the darkened patch. The Y separation alone isn't
    // enough — at some camera angles the distance-sort flips. See the
    // matching comment in makeContactShadow.
    mesh.renderOrder = 1;
    mesh.visible = false;            // toggled by refreshGlowFloor
    return mesh;
  }

  // Floor halo logic
  // ----------------
  // The halo's COLOUR is taken from whichever Glow module sits
  // CLOSEST to the bulb (y = 0.150 m) — not necessarily the one at
  // the bottom of the stack. A middle or top Glow module is what
  // the user sees as "the lit element", so its colour drives the
  // floor wash.
  //
  // The halo's STRENGTH scales by the same distanceFalloff curve
  // as the per-module halos, so a Glow module far from the bulb
  // produces only a faint floor tint (or none, if past
  // MAX_GLOW_DISTANCE).
  refreshGlowFloor () {
    if (!this.glowFloor) return;
    if (!this.isDark()) { this.glowFloor.visible = false; return; }

    // Find the Glow module whose cached world-y centre is closest
    // to the bulb. Falls back to "no halo" if none is found in
    // range.
    let bestMod      = null;
    let bestCenterY  = 0;
    let bestDistance = Infinity;
    for (const inst of this.partsGroup.children) {
      if (inst.userData.monoKind !== 'module') continue;
      const idx = inst.userData.monoIdx;
      const mod = this.modules[idx];
      if (!mod || mod.finishId !== 'glow') continue;
      const cy = inst.userData.monoCenterY ?? 0;
      const d  = Math.abs(cy - 0.150);
      if (d < bestDistance) {
        bestDistance = d;
        bestMod      = mod;
        bestCenterY  = cy;
      }
    }
    if (!bestMod) { this.glowFloor.visible = false; return; }

    const finish   = this.config.finishes.find(f => f.id === 'glow');
    const colorDef = finish?.colors[bestMod.colorIdx];
    if (!colorDef) { this.glowFloor.visible = false; return; }

    const falloff = this.distanceFalloff(bestCenterY);
    if (falloff <= 0.05) { this.glowFloor.visible = false; return; }

    // A faint pool of light directly under the lamp — kept low so it
    // doesn't read as a bright patch floating in the dark viewport.
    const BASE_OPACITY = 0.14;
    this.glowFloor.material.color.set(colorDef.ext);
    this.glowFloor.material.opacity = BASE_OPACITY * falloff;
    this.glowFloor.visible = true;
  }

  refreshOutline () {
    if (!this.outlinePass) return;
    const { kind, idx } = this.activeSlot || {};
    if (kind == null || idx == null) {
      this.outlinePass.selectedObjects = [];
      return;
    }
    const found = this.partsGroup.children.find(c =>
      c.userData.monoKind === kind && c.userData.monoIdx === idx
    );
    if (!found) {
      this.outlinePass.selectedObjects = [];
      return;
    }
    // OutlinePass traverses `selectedObjects` recursively for its
    // depth + normal pass to do edge detection. Passing the whole
    // module instance dragged in the halo Sprite too — sprites
    // are camera-facing billboards and don't write the kind of
    // depth+normal data the outline shader expects, which left
    // parts of the silhouette unhighlighted depending on camera
    // angle. Filter to MESHES only.
    const meshes = [];
    found.traverse(obj => { if (obj.isMesh) meshes.push(obj); });
    this.outlinePass.selectedObjects = meshes;
  }

  canvasSize () {
    const p = this.canvas.parentElement;
    return { width: p.clientWidth, height: p.clientHeight };
  }

  onResize () {
    const { width: w, height: h } = this.canvasSize();
    if (!w || !h) return;
    this.camera.aspect = w / h;
    // recomputeCameraPan re-evaluates desktop / fullscreen / mobile
    // and re-bakes the projection matrix via applyViewOffset (which
    // calls setViewOffset or clearViewOffset depending on state).
    // setViewOffset internally calls updateProjectionMatrix, and
    // clearViewOffset also re-bakes — so no separate call is needed.
    this.recomputeCameraPan();
    if (!this.cameraPanX) this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
    if (this.composer)  this.composer.setSize(w, h);
    if (this.bloomPass) this.bloomPass.setSize(w, h);
    // Re-frame so the whole lamp stays in view whatever the new
    // viewport shape is (mobile sheet shrinking the viewer, window
    // resizes, expanded bottom bar) — the camera is fixed-scale, so
    // this never fights the user.
    this.fitCameraToStack();
    // Static float card — re-anchor to the new viewport geometry.
    // (fitCameraToStack already re-anchored the FABs.)
    this.updateFloatPos();
  }

  // The camera is fixed and the lamp rotates continuously — nothing
  // to pause/resume any more. Kept as a no-op so mutation call sites
  // stay uniform.
  nudgeIdle () {}

  animate () {
    requestAnimationFrame(this.animate);
    this.advancePops();
    this.controls.update();
    if (this.composer) this.composer.render();
    else this.renderer.render(this.scene, this.camera);
    this.updateDimensionsOverlay();
  }

  // (Day/night toggle lives in the bottom-centre toolbar now — no
  // per-frame positioning needed.)

  // ---- Add-module FABs -------------------------------------------
  // Pin the two "+" buttons just above / below the lamp. On-axis
  // points project rotation-invariantly, so this runs only after
  // camera fits / rebuilds / resizes — not per frame.
  // Refresh the camera's world/inverse matrices RIGHT NOW. The
  // renderer only updates them during the next frame, so any
  // projection done straight after fitCameraToStack (or a rebuild)
  // would otherwise use the PREVIOUS camera pose — which is exactly
  // how the add-FABs kept landing in the middle of the lamp.
  freshenCameraMatrices () {
    if (!this.camera) return;
    this.camera.updateMatrixWorld(true);
    this.camera.matrixWorldInverse.copy(this.camera.matrixWorld).invert();
  }

  updateAddFabs () {
    const top = this.root.querySelector('[data-cfg-add-top]');
    const bot = this.root.querySelector('[data-cfg-add-bottom]');
    if (!top || !bot || !this.camera) return;
    this.freshenCameraMatrices();

    const canAdd = this.canAddModule();
    for (const el of [top, bot]) {
      el.disabled = !canAdd;
      el.classList.toggle('is-disabled', !canAdd);
      el.dataset.tip = canAdd
        ? (el === top ? 'Add module on top' : 'Add module below')
        : `Maximum height reached (${this.config.maxHeightMm} mm)`;
    }
    // Hidden while: the guide shows, the stack is empty (the centred
    // CTA takes over), or the scene hasn't finished its first rebuild
    // (no cached stack top yet → positioning would be a guess that
    // later "jumps"; the fit that follows the rebuild re-runs this).
    const hide = this.showGuide
      || this.modules.length === 0
      || this._stackTopY == null;
    top.toggleAttribute('hidden', hide);
    bot.toggleAttribute('hidden', hide);
    const empty = this.root.querySelector('[data-cfg-empty]');
    if (empty) empty.toggleAttribute('hidden', this.showGuide || this.modules.length > 0);
    if (hide) return;

    const rect = this.canvas.getBoundingClientRect();
    const v = (this._tmpFabVec ||= new THREE.Vector3());
    // Clamp band: the bottom of the stage is OWNED by the UI stack
    // (toolbar + combinations + hint ≈ 12rem) — the bottom "+" must
    // never enter it, even when a tall build's base projects low on a
    // narrow/tall viewport. Same idea up top for the header.
    const minY = 70;
    const maxY = rect.height - 215;
    const place = (el, worldY, pxOffset) => {
      v.set(0, worldY, 0).project(this.camera);
      if (v.z > 1) return;
      const sy = (-v.y * 0.5 + 0.5) * rect.height + pxOffset;
      el.style.left = rect.width / 2 + 'px';
      el.style.top  = Math.min(Math.max(sy, minY), maxY) + 'px';
    };
    place(top, this._stackTopY, -58);
    place(bot, 0, 62);
  }

  // ---- Arrangements browser --------------------------------------
  // Every distinct way the CURRENT parts can be stacked: unique
  // permutations of the module multiset × (compact | semi). The
  // widget shows "n / total" + a progress bar; prev/next re-stack
  // the lamp live (with the pop-in animation).
  computeArrangements () {
    const keyOf = m => `${m.sizeId}|${m.finishId}|${m.colorIdx}`;
    // CANONICAL base: the module objects sorted by key. The
    // enumeration then depends only on WHAT parts are in the build,
    // not their current order — so "n / total" stays stable while
    // browsing.
    const canon = [...this.modules].sort((a, b) =>
      keyOf(a) < keyOf(b) ? -1 : keyOf(a) > keyOf(b) ? 1 : 0);
    const keys = canon.map(keyOf);
    const perms = [];
    const used = new Array(keys.length).fill(false);
    const path = [];
    const walk = () => {
      if (perms.length >= 360) return;             // safety cap
      if (path.length === keys.length) { perms.push([...path]); return; }
      const seen = new Set();
      for (let i = 0; i < keys.length; i++) {
        if (used[i] || seen.has(keys[i])) continue; // dedupe identical parts
        seen.add(keys[i]);
        used[i] = true; path.push(i);
        walk();
        used[i] = false; path.pop();
      }
    };
    if (keys.length) walk();
    // Grouped by mode: ALL orderings in compact first, then the same
    // run again in semi — browsing doesn't flip compact/semi on every
    // step.
    const modes = ['compact', 'semi'];
    const variants = [];
    modes.forEach(mode => perms.forEach(p => variants.push({
      perm: p,
      mode,
      seq: p.map(i => keys[i]).join(',')
    })));
    return { variants, canon, keyOf };
  }

  renderArrangements () {
    const box = this.root.querySelector('[data-cfg-arr]');
    if (!box) return;
    const { variants, canon, keyOf } = this.computeArrangements();
    this._arrVariants = variants;
    this._arrCanon = canon;
    // Only interesting with 2+ distinct results and outside the guide.
    const usable = !this.showGuide && variants.length >= 2 && this.modules.length >= 2;
    // Desktop right-rail line ("With your setup you can build N…") —
    // mirrors the strip's visibility, count = all arrangements.
    const railCell = this.root.querySelector('[data-cfg-bar-combi]');
    if (railCell) {
      railCell.toggleAttribute('hidden', !usable);
      const cEl = railCell.querySelector('[data-cfg-combi-count]');
      if (cEl) cEl.textContent = String(variants.length);
    }
    if (!usable) {
      box.setAttribute('hidden', '');
      return;
    }
    box.removeAttribute('hidden');
    const currentSeq = this.modules.map(keyOf).join(',');
    let idx = variants.findIndex(v => v.mode === this.viewMode && v.seq === currentSeq);
    if (idx < 0) idx = 0;   // e.g. exploded view — not part of the cycle
    this._arrIdx = idx;
    const count = this.root.querySelector('[data-cfg-arr-count]');
    if (count) count.textContent = `${idx + 1} / ${variants.length}`;
    // Gallery-style tick indicator. Large builds can have 100+
    // combinations — cap the rendered ticks and let each tick stand
    // for a BUCKET of variants (click jumps to the bucket's start).
    const segs = this.root.querySelector('[data-cfg-arr-segs]');
    if (segs) {
      const MAX_SEGS = 24;
      const n = variants.length;
      const segCount = Math.min(n, MAX_SEGS);
      const bucket = n / segCount;
      const activeSeg = Math.min(segCount - 1, Math.floor(idx / bucket));
      segs.innerHTML = Array.from({ length: segCount }, (_, i) =>
        `<button type="button" class="cfg__arr-seg${i === activeSeg ? ' is-active' : ''}"
          data-arr-seg="${Math.round(i * bucket)}" aria-label="Combination group ${i + 1}"></button>`
      ).join('');
    }
  }

  // "View all" — schematic side-by-side previews of every arrangement
  // in the detail modal. Real 3D thumbnails would need one offscreen
  // render per variant; these stacked colour bars are instant and read
  // clearly. Click one to apply it.
  openArrangementsModal () {
    const modal = this.root.querySelector('[data-cfg-modal]');
    const body  = this.root.querySelector('[data-cfg-modal-body]');
    const variants = this._arrVariants || [];
    const canon = this._arrCanon || [];
    if (!modal || !body || !variants.length) return;
    // Second click on COMBINATIONS toggles the modal closed.
    if (this._modalKey === 'arr' && modal.classList.contains('is-open')) { this.closeModal(); return; }
    this._modalKey = 'arr';
    this.collapseBar();
    const hPx = { S: 16, M: 24, L: 32 };
    const stackHtml = (v) => {
      // Top-down: last module in the array is the top of the lamp.
      const bars = [...v.perm].reverse().map(k => {
        const m = canon[k];
        const finish = this.config.finishes.find(f => f.id === m.finishId) || this.config.finishes[0];
        const c = finish.colors[m.colorIdx] || finish.colors[0];
        return `<span class="cfg__arrprev-bar${m.finishId === 'glow' ? ' is-glow' : ''}"
          style="height:${hPx[m.sizeId] || 22}px;background:${c.swatch || c.ext};"></span>`;
      }).join('');
      return `<span class="cfg__arrprev-stack${v.mode === 'semi' ? ' is-semi' : ''}">${bars}</span>`;
    };
    body.innerHTML = `
      <p class="cfg__modal-variant">${variants.length} combinations of your parts</p>
      <h3 class="cfg__modal-title">Same parts, every stack</h3>
      <p class="cfg__modal-note">All the ways your parts can stack — order × spacing. Same parts, same price. Pick one and the lamp re-stacks live.</p>
      <div class="cfg__arrprev-grid">
        ${variants.map((v, i) => `
          <button type="button" class="cfg__arrprev${i === this._arrIdx ? ' is-active' : ''}" data-arr-apply="${i}">
            ${stackHtml(v)}
            <span class="cfg__arrprev-num">${i + 1}</span>
            <span class="cfg__arrprev-mode">${v.mode}</span>
          </button>`).join('')}
      </div>`;
    body.querySelectorAll('[data-arr-apply]').forEach(b =>
      b.addEventListener('click', () => {
        this.applyArrangement(+b.dataset.arrApply);
        this.closeModal();
      }));
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => modal.classList.add('is-open'));
  }

  applyArrangement (i) {
    const variants = this._arrVariants || [];
    const canon = this._arrCanon || [];
    if (!variants.length || !canon.length) return;
    const n = variants.length;
    const idx = ((i % n) + n) % n;                  // wrap around
    const v = variants[idx];
    // Re-stack from the canonical base (same module objects, new order;
    // connectors stay — their count is unchanged).
    this.modules = v.perm.map(k => canon[k]);
    this.viewMode = v.mode;
    this.activeSlot = { kind: null, idx: null };
    this._popAll = true;
    this.renderModeButtons();
    this.renderStack();
    this.rebuildScene().then(() => { this.fitCameraToStack(); this.updateAddFabs(); });
    this.tickPrice();
    this.renderDimensionsPanel();
    this.syncStateToUrl();
  }

  // ---- Module pop-in animation ---------------------------------
  // When a module appears or changes, it scales up from ~0.6 into
  // place with an ease-out "pop" (the effect the user liked on the
  // homepage preview). rebuildScene() registers the pivots to animate
  // via queuePop(); advancePops() drives them every frame.
  queuePop (pivot, delay = 0) {
    if (!pivot) return;
    (this._pops ||= []).push({
      pivot, delay,
      t: -delay,
      dur: 0.36,
    });
    pivot.scale.setScalar(0.0001);
  }
  advancePops () {
    if (!this._pops || !this._pops.length) return;
    const now = performance.now();
    const dt = this._lastPopTime ? Math.min((now - this._lastPopTime) / 1000, 0.05) : 0.016;
    this._lastPopTime = now;
    this._pops = this._pops.filter(p => {
      p.t += dt;
      if (p.t <= 0) { p.pivot.scale.setScalar(0.0001); return true; }
      const k = Math.min(p.t / p.dur, 1);
      // easeOutBack for a subtle overshoot "pop"
      const c1 = 1.70158, c3 = c1 + 1;
      const e = 1 + c3 * Math.pow(k - 1, 3) + c1 * Math.pow(k - 1, 2);
      p.pivot.scale.setScalar(0.6 + 0.4 * e / 1.0 * (1 / 1));
      // normalise: at k=1 e≈1 → scale 1.0
      p.pivot.scale.setScalar(0.55 + 0.45 * e);
      return k < 1;
    });
    if (!this._pops.length) this._lastPopTime = 0;
  }

  // The reorder arrow/drag column is CSS-anchored to the left of the
  // floating editor now — no per-frame projection. This just toggles
  // its visibility (modules only) and the end-of-stack disabled states.
  updateOverlayArrows () {
    if (!this.overlayArrows) return;
    const { kind, idx } = this.activeSlot || {};
    if (kind !== 'module' || idx == null) {
      this.overlayArrows.setAttribute('hidden', '');
      return;
    }
    this.overlayArrows.removeAttribute('hidden');
    const upBtn   = this.overlayArrows.querySelector('[data-action="overlay-move-up"]');
    const downBtn = this.overlayArrows.querySelector('[data-action="overlay-move-down"]');
    if (upBtn)   upBtn.disabled   = idx >= this.modules.length - 1;
    if (downBtn) downBtn.disabled = idx <= 0;
  }

  // World-space bbox of a subtree, considering only Mesh children
  // (Sprite, PointLight, Group etc. are SKIPPED). Used by both the
  // overlay-arrow anchoring and the dimension koty so the cosmetic
  // glow-halo sprite doesn't inflate the bounding box and push the
  // arrows / dimension lines off the actual lamp geometry.
  //
  // Caller must ensure matrixWorld is current on the subtree
  // (rebuildScene runs after all positioning is in place, so the
  // matrices have been updated by the next render frame).
  computeMeshBox (root, target) {
    target.makeEmpty();
    const tmp = (this._tmpMeshBoxScratch ||= new THREE.Box3());
    root.traverse(obj => {
      if (!obj.isMesh || !obj.geometry) return;
      if (!obj.geometry.boundingBox) obj.geometry.computeBoundingBox();
      tmp.copy(obj.geometry.boundingBox).applyMatrix4(obj.matrixWorld);
      target.union(tmp);
    });
    return target;
  }

  // Computes total stack height including connectors+gap, returns midpoint.
  //
  // Preferred path: measure the actual world-space bbox of `partsGroup`
  // — that's the source of truth post-rebuildScene, and it tolerates
  // GLBs whose true extents drift from config.heights (per-pattern
  // uploads in particular).
  //
  // Fallback path (no parts yet, or empty group): estimate from
  // config.heights + the configured gap. Used only for the first-paint
  // camera fit before any model has loaded.
  stackMetrics () {
    if (this.partsGroup && this.partsGroup.children.length) {
      // Neutralise any in-flight pop scaling so the fit measures the
      // FULL-size stack (pops leave pivots at ~0.0001 for a frame,
      // which would otherwise make fitCameraToStack zoom into a speck).
      const popped = this.partsGroup.children.filter(c => c.scale.x < 0.999);
      const saved = popped.map(c => c.scale.x);
      popped.forEach(c => c.scale.setScalar(1));
      // Force world-matrix refresh before reading the bbox.
      // stackMetrics is called from fitCameraToStack right after
      // rebuildScene completes — at that point the renderer
      // hasn't done its automatic updateMatrixWorld pass yet, so
      // the freshly-positioned module instances have stale
      // matrices. Three.js's Box3.setFromObject does this
      // implicitly; our custom computeMeshBox doesn't.
      this.partsGroup.updateMatrixWorld(true);
      const box = this.computeMeshBox(this.partsGroup, this._tmpStackBox ||= new THREE.Box3());
      popped.forEach((c, i) => c.scale.setScalar(saved[i]));
      this.partsGroup.updateMatrixWorld(true);
      const totalHeight = box.max.y - box.min.y;
      const midY        = (box.max.y + box.min.y) / 2;
      return { totalHeight, midY };
    }
    const gap = this.config.gaps[this.viewMode] ?? 0;
    let totalH = 0;
    this.modules.forEach(m => { totalH += this.config.heights[m.sizeId]; });
    if (this.modules.length > 1) totalH += (this.modules.length - 1) * gap;
    return { totalHeight: totalH, midY: totalH / 2 };
  }

  fitCameraToStack () {
    const { totalHeight, midY } = this.stackMetrics();
    const fovRad = (this.camera.fov * Math.PI) / 180;
    // CONSTANT scale: frame the maximum possible build height, not the
    // current one — a two-module vase reads small, a full tower reads
    // tall, and swapping between them never re-zooms. (Exploded view
    // can exceed the cap, so take the larger of the two.)
    // Portrait (mobile) frames the lamp noticeably smaller — the
    // bottom UI stack plus browser chrome leave less real room than
    // the raw viewport suggests.
    const { width: vw0, height: vh0 } = this.canvasSize();
    const margin = vh0 > vw0 ? 2.05 : 1.7;
    const refHeight = Math.max((this.config.maxHeightMm || 565) / 1000, totalHeight);
    const fitDist = (refHeight * margin) / (2 * Math.tan(fovRad / 2));
    const dist = Math.max(0.6, fitDist);
    // Orbit pivot = model centre, so the lamp rotates on its own axis.
    // The target sits slightly BELOW the model centre so the lamp rides
    // higher in the frame — clearing the add-FAB / toolbar / dimension
    // labels at the bottom of the stage. Portrait viewports (mobile)
    // need a stronger lift: the bottom UI stack eats proportionally
    // more height there, so without it the base FAB lands ON the lamp.
    const lift = refHeight * (vh0 > vw0 ? 0.11 : 0.06);
    this.controls.target.set(0, midY - lift, 0);
    this.camera.position.set(
      dist * 0.32,
      (midY - lift) + dist * 0.10,
      dist * 0.92
    );
    this.controls.update();
    // FABs + the open editor card hug the lamp — re-anchor them after
    // the camera settles (the card would otherwise keep a stale
    // position when a module was just added and its mesh finished
    // loading only now).
    this.updateAddFabs();
    this.updateFloatPos();
  }

  // Shifts the rendered viewport horizontally without moving the
  // camera or its orbit pivot. Re-applied on every resize so the
  // pan offset scales with the canvas. Pass `cameraPanX = 0` to
  // restore a centred frame.
  applyViewOffset () {
    const { width, height } = this.canvasSize();
    if (!this.cameraPanX) {
      this.camera.clearViewOffset();
      return;
    }
    // Three.js setViewOffset(fullWidth, fullHeight, x, y, width, height):
    //   the camera renders a sub-rectangle (x, y, width, height) of a
    //   virtual frustum (fullWidth, fullHeight). The optical centre of
    //   the full frustum is at (fullWidth/2, fullHeight/2). For the
    //   target (= model centre, at world origin) to project to
    //   fraction `f` from the visible region's left edge:
    //     offsetX = fullWidth/2 - f * subWidth
    //   With fullWidth = subWidth = canvas width and f = 0.5 - panX:
    //     offsetX = panX * canvasWidth
    this.camera.setViewOffset(
      width, height,
      this.cameraPanX * width, 0,
      width, height
    );
  }

  // Decide whether the camera should be off-centre. The pan only
  // makes sense when the right-side panel is occupying viewport
  // space — fullscreen hides the panel entirely, and below the
  // mobile breakpoint the panel stacks BELOW the canvas (so it
  // doesn't compete with the lamp horizontally). Both cases want a
  // centred camera. Re-evaluated on init, fullscreen toggle and
  // every resize.
  recomputeCameraPan () {
    // Full-width viewport now — the lamp sits dead centre. (The old
    // right-panel layout shifted it left via an asymmetric frustum.)
    this.cameraPanX = 0;
    this.applyViewOffset();
  }

  // ============== Model loading ==============

  async loadModel (key) {
    if (this.modelCache.has(key)) return this.modelCache.get(key);
    const ref = this.config.models[key];
    if (!ref) throw new Error('no model for key ' + key);
    // The reference may be:
    //   • A FULL URL (https://… or //…) — emitted by the Shopify
    //     Liquid layer when a part product has a file_reference
    //     metafield pointing to a CDN-uploaded GLB. Liquid's
    //     `metafield.value.url` returns the protocol-relative form
    //     `//<shop>.myshopify.com/cdn/shop/files/…`, so we accept
    //     both `https://` and the bare `//` prefix.
    //   • An absolute path (`/cdn/...`) — same source as above on
    //     some Shopify routing paths.
    //   • A bare filename — emitted by the bundled
    //     configurator-config.js as a fallback when no Shopify
    //     override exists. We prepend assetBase to make it absolute.
    let url;
    if (/^https?:\/\//.test(ref))      url = ref;
    else if (ref.startsWith('//'))     url = 'https:' + ref;
    else if (ref.startsWith('/'))      url = ref;
    else                               url = this.config.assetBase + ref;
    let scene;
    try {
      const gltf = await this.gltfLoader.loadAsync(url);
      scene = gltf.scene;
    } catch (err) {
      // Defense-in-depth: when a Shopify-hosted GLB (cross-origin)
      // fails to load — bad CORS preflight, 404, malformed bytes,
      // Draco decoder mismatch — we fall back to the bundled GLB
      // shipped under /assets so the viewer never renders empty.
      // The bundled key still exists in DEFAULT_CONFIG.models, so
      // mergeShopifyCatalogue's per-key merge guarantees there's a
      // sibling entry on the bare-filename path.
      // eslint-disable-next-line no-console
      console.warn('[configurator] loadModel "%s" failed for %s — falling back to bundled.', key, url, err);
      const fallback = (DEFAULT_CONFIG.models || {})[key];
      if (!fallback) throw err;
      const fbUrl = /^https?:\/\//.test(fallback) ? fallback : (this.config.assetBase + fallback);
      const gltf = await this.gltfLoader.loadAsync(fbUrl);
      scene = gltf.scene;
    }
    this.modelCache.set(key, scene);
    return scene;
  }

  // Authoritative vertical extent of a cached GLB. Measured once on
  // first use and stashed on the cached scene, so subsequent clones
  // don't re-traverse the geometry.
  //
  // Returns:
  //   height — bbox.max.y - bbox.min.y (metres, world-space of the GLB
  //            at its authored scale — Three.js applies no implicit
  //            scaling here)
  //   yMin   — signed offset from the GLB's local origin to the BOTTOM
  //            of its geometry. Used to align the bottom of the
  //            module to its target stack-y regardless of where the
  //            modeller placed the origin (centre / bottom / arbitrary).
  //
  // This replaces the prior reliance on `config.heights[sizeId]`, which
  // could drift from the GLB's true extent (different uploaded patterns,
  // edits to the model without a matching config bump, etc.) and caused
  // asymmetric gap / overlap between stacked modules.
  modelExtent (cached) {
    if (!cached.userData.laseExtent) {
      const box = new THREE.Box3().setFromObject(cached);
      cached.userData.laseExtent = {
        height: box.max.y - box.min.y,
        yMin:   box.min.y,
        yMax:   box.max.y
      };
    }
    return cached.userData.laseExtent;
  }

  cloneModel (cached) {
    const clone = cached.clone(true);
    const colorables = { ext: [], int: [], connector: [] };
    clone.traverse(obj => {
      if (!obj.isMesh || !obj.material) return;
      // Every module mesh casts a shadow onto the catcher; they don't
      // receive (self-shadowing on the thin fins looked noisy — the env
      // occlusion already gives them form).
      obj.castShadow = true;
      obj.receiveShadow = false;
      const name = obj.material.name || '';
      if (!name.startsWith('colorable')) return;
      obj.material = obj.material.clone();

      // PBR materials read scene.environment for image-based ambient.
      // Per-group tuning so the inner cup doesn't behave like a
      // mirror in dark mode (which produced an unwanted halo around
      // white INT surfaces):
      //   ext       — moderate reflections, the painted finish reads
      //               with realistic depth.
      //   int       — heavily suppressed env + cranked roughness so
      //               the cup behaves like a matte print, even when
      //               its colour is pure white.
      //   connector — close to ext, but slightly subdued.
      if (name.includes('ext')) {
        // Plain clone — back to the simple MeshStandardMaterial
        // pipeline. Glow look is now driven by a stylised additive
        // halo sprite (see makeGlowSprite), not by transmission or
        // alpha tricks on the material itself. The shell stays
        // opaque coloured plastic regardless of finish.
        obj.material = obj.material.clone();
        if ('envMapIntensity' in obj.material) obj.material.envMapIntensity = 0.55;
        colorables.ext.push(obj.material);
      } else if (name.includes('int')) {
        if ('envMapIntensity' in obj.material) obj.material.envMapIntensity = 0.12;
        if ('roughness'       in obj.material) obj.material.roughness       = 0.95;
        if ('metalness'       in obj.material) obj.material.metalness       = 0;
        colorables.int.push(obj.material);
      } else if (name.includes('connector')) {
        if ('envMapIntensity' in obj.material) obj.material.envMapIntensity = 0.40;
        colorables.connector.push(obj.material);
      }
    });
    return { instance: clone, colorables };
  }

  // Falloff factor 0..1 for a module's distance to the fixed bulb.
  // Linear, clamps at zero past MAX_GLOW_DISTANCE. Used by both
  // material emissives and the halo sprite so far-away modules
  // fade to "no glow at all" — even if their finish is Glow.
  //
  //   worldY = the module's world-space vertical centre
  //   bulb   = (0, 0.150, 0) in world space
  //
  // MAX_GLOW_DISTANCE = 0.30 m means a Glow module roughly
  // beyond an M-module-height away from the bulb stops glowing.
  // Tune this constant to widen or tighten the lit zone.
  distanceFalloff (worldY) {
    // Position-independent for now: every Glow module glows fully
    // regardless of where it sits in the stack. Once the real light
    // module ships we'll drive luminance from its actual position.
    return 1;
  }

  applyModuleColor (colorables, color, finishId, falloff = 1) {
    const dark   = this.isDark();
    const isGlow = finishId === 'glow';
    const extCol = new THREE.Color(color.ext);
    // INT (inner cup) is ALWAYS the matte plastic — printed in the
    // matte filament of the same colour regardless of the shell's
    // finish. Resolve it from the matte palette by colour id; the
    // glow palette's lightened int hex is only a fallback.
    const matteDef = this.config.finishes
      .find(f => f.id === 'matte')?.colors.find(c => c.id === color.id);
    const intCol = new THREE.Color((matteDef && matteDef.ext) || color.int || color.ext);

    // Per-colour luma normalisation
    // ----------------------------------------------------------
    // Bloom pixel-selection is driven by LUMA (perceived
    // brightness), not by emissiveIntensity alone. The effective
    // pixel value after emissive = color × intensity, and bloom
    // catches pixels whose luma exceeds the threshold (1.0).
    //
    // Different swatches have wildly different lumas:
    //   yellow #c9a449 → luma ≈ 0.65   (high)
    //   pink   #d76b3a → luma ≈ 0.55   (high)
    //   blue   #3b5b8b → luma ≈ 0.32   (low — barely blooms)
    //   green  #3f5b3a → luma ≈ 0.29   (low — doesn't bloom)
    //
    // A fixed emissiveIntensity therefore produces wildly
    // uneven halos: yellows blow out, blues stay dead. To
    // unify, we scale intensity INVERSELY by the swatch's luma
    // so every swatch reaches the same target luma post-multiply.
    //
    //   intensity = TARGET_LUMA / max(swatch_luma, 0.15)
    //
    // The max() clamp prevents near-black swatches from
    // exploding the intensity unboundedly. Rec.709 coefficients.
    // Plain LDR emissive — back to basics. The visible halo /
    // "lit from within" effect is delivered by an additive halo
    // sprite (see makeGlowSprite) attached to each Glow module,
    // NOT by the material itself. EXT and INT just get gentle
    // emissives so the surfaces read as slightly self-tinted in
    // dark mode; neither drives bloom (all values below the
    // bloom threshold). Colour uniformity across the palette is
    // handled by the sprite's own additive-blend math, which
    // doesn't care about swatch luma the way bloom-thresholding
    // does.
    // Luma-normalised emissive target so EVERY colour reaches the same
    // perceived brightness and blooms evenly (a dark blue and a bright
    // yellow both read as genuinely lit-from-within, not one blown out
    // and the other dead). pixel_luma ≈ luma(color) × intensity, so
    // intensity = target / luma. Rec.709 coefficients; clamp keeps
    // near-black swatches from exploding.
    // Gentle luma normalisation with a LOW ceiling so low-luma colours
    // (blue, violet) glow softly in their own hue instead of being
    // driven to near-white intensity that blooms into a big grey haze.
    // Spectrum-flat emissive: pick an intensity so the shell reaches a
    // fixed PERCEIVED brightness (targetLuma) after the colour multiply,
    // regardless of hue. pixel_luma ≈ luma(colour) × intensity, so
    // intensity = targetLuma / luma(colour). Low-luma hues (red, deep
    // blue, violet) get a big multiplier, high-luma hues (yellow) a
    // small one — so red and yellow bloom EQUALLY instead of red barely
    // glowing while yellow blows out. Generous ceiling so even a very
    // dark hue can reach the target; target itself is ~1/4 of the first
    // iteration so the glow is gentle.
    const lumaOf = (c) => 0.2126 * c.r + 0.7152 * c.g + 0.0722 * c.b;
    const emissiveFor = (c, targetLuma) => {
      const L = Math.max(lumaOf(c), 0.05);
      return Math.min(targetLuma / L, 7.0);
    };

    colorables.ext.forEach(m => {
      m.color.set(extCol);
      m.emissive = m.emissive || new THREE.Color();
      if (isGlow && dark) {
        // Shell glows softly from within — dialled well down from the
        // first pass (which was too intense), so it's a calm lit glow,
        // not a floodlight.
        m.emissive.copy(extCol);
        m.emissiveIntensity = emissiveFor(extCol, 0.26) * falloff;
      } else if (isGlow) {
        // Day: a whisper of self-tint so translucent PETG looks alive
        // even before the transmission pass.
        m.emissive.copy(extCol);
        m.emissiveIntensity = 0.06;
      } else {
        m.emissive.set(0x000000);
        m.emissiveIntensity = 0;
      }
      if ('envMapIntensity' in m) {
        // EXACT homepage-preview reflection strength.
        m.envMapIntensity = isGlow ? 0.55 : 0.20;
      }
      // FULLY OPAQUE — including glow. The old near-opaque transparency
      // (0.98) added no visible see-through but forced the transparent
      // render path, whose depth self-sorting produced dark z-fighting
      // streaks on the thin-walled inner cup. The glow look comes from
      // the light palette colour + emissive + bloom, none of which need
      // transparency.
      m.transparent = false;
      m.opacity     = 1.0;
      m.depthWrite  = true;
      if ('transmission' in m) m.transmission = 0;
      // EXACT homepage-preview material response (the user's reference):
      // matte PLA satin (rough 0.88), glow PETG glossier satin (0.45).
      if ('roughness' in m) m.roughness = isGlow ? 0.45 : 0.88;
      if ('metalness' in m) m.metalness = 0;
    });
    colorables.int.forEach(m => {
      m.color.set(intCol);
      m.emissive = m.emissive || new THREE.Color();
      if (isGlow && dark) {
        // Inner cup a touch brighter than the shell so the light reads
        // as coming from within — but gently.
        m.emissive.copy(intCol);
        m.emissiveIntensity = emissiveFor(intCol, 0.36) * falloff;
      } else if (isGlow) {
        m.emissive.copy(intCol);
        m.emissiveIntensity = 0.1;
      } else {
        m.emissive.set(0x000000);
        m.emissiveIntensity = 0;
      }
      // Opaque inner cup too — the 0.94 translucency was the source of
      // the artefacts the user saw inside glow modules (front + back
      // cup walls z-fighting through each other).
      m.transparent = false;
      m.opacity     = 1.0;
      m.depthWrite  = true;
      if ('roughness' in m) m.roughness = isGlow ? 0.5 : 0.95;
      if ('envMapIntensity' in m) m.envMapIntensity = isGlow ? 0.55 : 0.12;
      if ('metalness' in m) m.metalness = 0;
    });
  }

  // Soft additive halo around a Glow module — purely cosmetic
  // "fake glow" approach.
  //
  // Sprite anatomy:
  //   • Single shared radial-gradient texture (white centre →
  //     transparent edge), baked once on first use.
  //   • SpriteMaterial in AdditiveBlending mode tints the
  //     gradient with the swatch colour, which is then ADDED to
  //     whatever's behind the sprite in the framebuffer. Because
  //     it's additive, the result is the swatch tint laid on top
  //     of the existing pixel — naturally airy / mist-like.
  //
  // Why this approach is universal (no per-colour tuning):
  //   • Additive blending means saturation, not perceived
  //     brightness, drives the halo intensity.  A blue sprite
  //     adds blue light; a yellow sprite adds yellow light. Both
  //     create a visible halo of equivalent visual mass.
  //   • The single global `opacity` knob (HALO_OPACITY below)
  //     scales the strength uniformly across every colour.
  //   • New colours added to the palette inherit the same look
  //     automatically — no luma normalisation, no thresholds,
  //     no hardcoded per-swatch overrides.
  //
  // Sprite size scales with the module's bbox so big modules
  // get proportionally bigger halos.
  glowHaloTexture () {
    if (this._haloTexture) return this._haloTexture;
    const size = 256;
    const cnv = document.createElement('canvas');
    cnv.width = size; cnv.height = size;
    const ctx = cnv.getContext('2d');
    const g = ctx.createRadialGradient(size/2, size/2, 0, size/2, size/2, size/2);
    g.addColorStop(0.00, 'rgba(255,255,255,1.00)');
    g.addColorStop(0.20, 'rgba(255,255,255,0.55)');
    g.addColorStop(0.50, 'rgba(255,255,255,0.18)');
    g.addColorStop(1.00, 'rgba(255,255,255,0.00)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, size, size);
    const tex = new THREE.CanvasTexture(cnv);
    tex.colorSpace = THREE.SRGBColorSpace;
    this._haloTexture = tex;
    return tex;
  }

  makeGlowSprite (color, extent, falloff = 1) {
    // The lamp's emissive surface + bloom now carry the glow, so this
    // sprite is just a faint outer bloom aid — not the big backdrop
    // blob it used to be. Cut way down.
    const HALO_OPACITY = 0.20;
    const mat = new THREE.SpriteMaterial({
      map:         this.glowHaloTexture(),
      color:       new THREE.Color(color),
      transparent: true,
      blending:    THREE.AdditiveBlending,
      depthWrite:  false,
      opacity:     HALO_OPACITY * falloff
    });
    const sprite = new THREE.Sprite(mat);

    // Size: roughly twice the module silhouette, taller than wide
    // so the halo bleeds beyond the lamellae's top and bottom.
    // bbox is in metres so sprite.scale (world units) maps directly.
    const w = extent ? (extent.height * 1.05 + 0.06) : 0.24;
    const h = extent ? (extent.height * 1.35 + 0.06) : 0.32;
    sprite.scale.set(w, h, 1);
    // Center vertically on the module bbox.
    sprite.position.y = extent ? (extent.yMin + extent.yMax) / 2 : 0.07;
    sprite.userData.monoKind = 'glow-sprite';
    return sprite;
  }

  // Single fixed "bulb" — one PointLight anchored at WORLD
  // (0, 0.150 m, 0) regardless of which modules are in the stack
  // or where Glow modules sit. Mimics a real desk-lamp where the
  // socket is at a fixed height inside the fixture.
  //
  // Lifecycle: removed + recreated on every rebuild (and on
  // applyTheme via rebuildScene) so colour + intensity track the
  // current state. If no Glow module is present the bulb is
  // simply not added.
  //
  // Colour: taken from the LOWEST Glow module in the stack — the
  // user's mental model is "the lit base colours the whole
  // fixture", which matches how a real bulb behind a coloured
  // shade behaves. If you want a fixed warm-white bulb instead
  // (so the colour comes purely from the plastic, not the bulb)
  // swap the colour line to e.g. 0xffd9a0.
  refreshCentralBulb () {
    if (this._centralBulb) {
      this.scene.remove(this._centralBulb);
      this._centralBulb = null;
    }
    if (this._centralBulbMesh) {
      this.scene.remove(this._centralBulbMesh);
      this._centralBulbMesh.geometry?.dispose?.();
      this._centralBulbMesh.material?.dispose?.();
      this._centralBulbMesh = null;
    }
    // Day-mode = no bulb at all. The lamp is just a coloured
    // object lit by the room; nothing should "be on". Same for an
    // empty stack (start guide / from scratch) — a lone glowing orb
    // floating in the dark viewport just looks broken.
    if (!this.isDark() || this.modules.length === 0) return;

    const BULB_COLOR = 0xffd9a0;

    // ---- The VISIBLE bulb mesh — ALWAYS in dark mode -------------
    // Created regardless of whether a Glow module is present.
    // When the bulb sits behind a Matte module (which is fully
    // opaque + writes depth), it's properly occluded by depth
    // test. When it sits behind a Glow module shell, it shows
    // through the natural GAPS between lamellae (where there's
    // no geometry to occlude it) — physically correct, no
    // depth-pass hackery needed.
    //
    // HDR multiplier 5.0 (was 3.0): well above the bloom
    // threshold so the bulb reads as a properly bright light
    // source with a clear bloom halo. toneMapped=false prevents
    // ACES from crushing the HDR punch.
    const bulbHdr = new THREE.Color(BULB_COLOR).multiplyScalar(5.0);
    const bulbMat = new THREE.MeshBasicMaterial({
      color:       bulbHdr,
      transparent: false,
      depthTest:   true,
      depthWrite:  true,
      toneMapped:  false
    });
    const bulbGeo = new THREE.SphereGeometry(0.012, 16, 12);
    const bulbMesh = new THREE.Mesh(bulbGeo, bulbMat);
    bulbMesh.position.set(0, 0.150, 0);
    bulbMesh.userData.monoKind = 'central-bulb-mesh';
    this.scene.add(bulbMesh);
    this._centralBulbMesh = bulbMesh;

    // ---- PointLight — only if a Glow module is present -----------
    // The PointLight tints the Glow module interiors warm white
    // (~2700 K) via layer scoping. Skipped when there's no glow
    // module since there's nothing on layer 1 to illuminate.
    const glowMod = (this.modules || []).find(m => m.finishId === 'glow');
    if (!glowMod) return;

    const light = new THREE.PointLight(
      new THREE.Color(BULB_COLOR),
      1.2,
      0.6,
      1.8
    );
    light.position.set(0, 0.150, 0);
    light.layers.set(1);
    light.userData.monoKind = 'central-bulb';
    this.scene.add(light);
    this._centralBulb = light;
  }

  applyConnectorColor (colorables, color) {
    const c = new THREE.Color(color.ext);
    colorables.connector.forEach(m => m.color.set(c));
  }

  // ============== Scene composition ==============

  async rebuildScene () {
    while (this.partsGroup.children.length) {
      this.partsGroup.remove(this.partsGroup.children[0]);
    }

    const gap = this.config.gaps[this.viewMode] ?? 0;

    // Stacking is driven by the ACTUAL bounding-box height of each
    // loaded GLB (see modelExtent), not by config.heights. Heights in
    // the config remain advisory — used for the spec panel + camera
    // pre-fit before models finish loading — but the renderer never
    // trusts them for placement, so a module whose printed size drifts
    // from the bundled constant still stacks flush.
    let y = 0;
    for (let i = 0; i < this.modules.length; i++) {
      const mod = this.modules[i];
      const finish = this.config.finishes.find(f => f.id === mod.finishId) || this.config.finishes[0];
      const color  = finish.colors[mod.colorIdx] || finish.colors[0];
      const shape  = this.config.shapes.find(s => s.id === mod.shapeId) || this.config.shapes[0];
      // Renderer applies the SHARED swatch hex — by design, a single
      // "Blue Matte" looks the same across S/M/L. The adapter
      // populates `color.ext` / `color.int` from the product-level
      // palette (defined once per finish in Shopify), so the merchant
      // only edits a colour in one place. Per-variant ERP data is
      // resolved separately in the cart-line builder via
      // `finish.variantMap[<pattern>/<size>/<color_id>]`.

      // Pattern-aware model key first (`module_P01_M`), then the
      // legacy size-only key (`module_M`) as a graceful fallback for
      // bundled GLBs that pre-date the per-pattern split.
      let cached;
      try {
        cached = await this.loadModel('module_' + (shape?.id || '') + '_' + mod.sizeId);
      } catch (_e) {
        cached = await this.loadModel('module_' + mod.sizeId);
      }
      const ext = this.modelExtent(cached);
      const { instance, colorables } = this.cloneModel(cached);
      // Wrap in a pivot centred on the module so the pop-in animation
      // scales around the module's middle (not its GLB origin). The
      // pivot carries the mono tagging; the instance sits inside it
      // offset so its bbox bottom still lands on the running stack-y.
      const moduleCentreY0 = y + ext.height / 2;
      const pivot = new THREE.Group();
      pivot.position.y = moduleCentreY0;
      instance.position.y = (y - ext.yMin) - moduleCentreY0;
      pivot.add(instance);
      pivot.userData.monoKind = 'module';
      pivot.userData.monoIdx  = i;

      // Distance falloff from the fixed bulb (world y = 0.150).
      // Module centre world y = stack-y (the running `y`) +
      // half the module's bbox height. The falloff factor scales
      // BOTH the material emissives and the halo sprite opacity
      // so the further the module from the bulb, the dimmer it
      // glows — eventually fading to zero past MAX_GLOW_DISTANCE.
      const moduleCentreY = y + ext.height / 2;
      const falloff = this.distanceFalloff(moduleCentreY);

      // Cache the world-y centre on the pivot so refreshGlowFloor
      // (called after the loop) can find which module sits closest
      // to the bulb without recomputing the stack layout.
      pivot.userData.monoCenterY = moduleCentreY;

      this.applyModuleColor(colorables, color, finish.id, falloff);

      // Enable layer 1 on every mesh in this Glow module so the
      // layer-scoped central bulb (refreshCentralBulb) can
      // illuminate them. Matte modules + connectors stay on the
      // default layer 0 only, so the bulb never visibly tints
      // them even when it's physically inside or behind them.
      if (finish.id === 'glow') {
        instance.traverse(obj => {
          if (obj.isMesh) obj.layers.enable(1);
        });
      }

      this.partsGroup.add(pivot);

      // Pop-in: whole stack on a full rebuild (randomise / preset),
      // else just the module(s) that changed. Staggered from the base.
      if (this._popAll) this.queuePop(pivot, i * 0.06);
      else if (this._popTargets && this._popTargets.has(i)) this.queuePop(pivot, 0);

      const topOfThis = y + ext.height;

      if (i < this.modules.length - 1) {
        const connector = this.connectors[i];
        const cnColor = this.config.connectorColors[connector?.colorIdx ?? 0] || this.config.connectorColors[0];
        const cn = await this.loadModel('connector');
        const cnExt = this.modelExtent(cn);
        const cnClone = this.cloneModel(cn);
        // Centre the connector's bbox on the gap midpoint. The same
        // origin-agnostic alignment as for modules: yOffset compensates
        // for whatever the modeller chose as the GLB origin.
        const gapCenter = topOfThis + gap / 2;
        cnClone.instance.position.y = gapCenter - (cnExt.yMin + cnExt.height / 2);
        cnClone.instance.userData.monoKind = 'connector';
        cnClone.instance.userData.monoIdx  = i;
        this.applyConnectorColor(cnClone.colorables, cnColor);
        this.partsGroup.add(cnClone.instance);
        if (this._popAll) this.queuePop(cnClone.instance, i * 0.06 + 0.03);
        y = topOfThis + gap;
      } else {
        y = topOfThis;
      }
    }

    // Pop flags are single-use — consumed by this rebuild.
    this._popAll = false;
    this._popTargets = null;

    if (this.shadowMesh) {
      this.shadowMesh.position.y = 0.001;
      // No lamp → no shadow (empty start-guide / from-scratch state).
      this.shadowMesh.visible = this.modules.length > 0;
    }
    // Re-tint the glow halo: in night mode, the bottom module's
    // colour drives the floor wash if that module is set to Glow.
    this.refreshGlowFloor();

    // The single fixed bulb (world y = 0.150 m). Removed / recreated
    // on every rebuild so colour + intensity reflect the current
    // Glow module and theme.
    this.refreshCentralBulb();

    // Cache the stack's top-Y (full size, pops neutralised inside
    // stackMetrics) — read every frame by updateDaynightPos to keep
    // the day/night button hovering right above the lamp.
    const m = this.stackMetrics();
    this._stackTopY = m.midY + m.totalHeight / 2;

    // After the scene is rebuilt, re-resolve the outline target since
    // mesh references have changed.
    this.refreshOutline();
  }

  // ============== UI rendering ==============

  buildUI () {
    this.renderModeButtons();
    this.renderStack();
    this.renderLightSection();
    this.wireControls();
  }

  renderModeButtons () {
    if (!this.modeList) return;
    this.modeList.innerHTML = '';
    // Icons show three stacked plates with increasing gaps so the
    // visual gesture matches the layout it produces.
    const modeIcon = (id) => {
      // Three pill-shaped plates centred on x=12; y positions vary by mode.
      const ys = id === 'compact'  ? [9, 12, 15]
               : id === 'semi'     ? [7, 12, 17]
               :                     [4, 12, 20];
      const rects = ys.map(y =>
        `<rect x="5" y="${y - 0.9}" width="14" height="1.8" rx="0.9"/>`
      ).join('');
      return `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">${rects}</svg>`;
    };
    [
      { id: 'compact',  label: 'Compact'  },
      { id: 'semi',     label: 'Semi'     },
      { id: 'exploded', label: 'Exploded' }
    ].forEach(m => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cfg__mode-btn' + (m.id === this.viewMode ? ' is-active' : '');
      btn.dataset.viewMode = m.id;
      btn.setAttribute('aria-label', m.label);
      btn.setAttribute('title', m.label);
      btn.innerHTML = modeIcon(m.id);
      this.modeList.appendChild(btn);
    });
  }

  isActive (kind, idx) {
    return this.activeSlot.kind === kind && this.activeSlot.idx === idx;
  }

  renderStack () {
    if (!this.slotList) return;

    // Fresh open → the "how do you want to start" guide floats in the
    // viewport until the user picks a path.
    if (this.showGuide) {
      this.root.classList.add('cfg--guide');
      this.renderGuide();
      this.renderConfigSummary();
      this.refreshCartValidity();
      this.updateHintPill();
      this.updateAddFabs();
      this.renderArrangements();
      return;
    }

    // No card list any more — modules are edited via the floating
    // in-viewport editor; the bottom-bar chips are the build overview.
    this.slotList.innerHTML = '';

    // Drop a selection that no longer points at an existing part
    // (e.g. the selected module was just removed).
    const { kind, idx } = this.activeSlot;
    const stale = (kind === 'module'    && !this.modules[idx])
               || (kind === 'connector' && !this.connectors[idx]);
    if (stale) this.activeSlot = { kind: null, idx: null };

    this.renderConfigSummary();   // also refreshes the bar chips
    this.renderFloatCard();       // re-render (or hide) the open editor
    this.refreshCartValidity();
    this.updateHintPill();
    this.updateAddFabs();
    this.renderArrangements();
  }

  // "Your build" summary — product cards for the configured MODULES,
  // rendered into a separate section below the configurator (the
  // [data-cfg-summary] host, see sections/lase-config-summary.liquid).
  // Each card shows the real per-variant photo + links to that
  // variant's PDP. Connectors are omitted (joiner hardware, no PDP
  // photo). No-op when the host section isn't on the page.
  renderConfigSummary () {
    const host = document.querySelector('[data-cfg-summary]');
    if (!host) return;
    const cfg = this.config;
    const sizesById = Object.fromEntries((cfg.sizes || []).map(s => [s.id, s]));
    // Match the homepage MONO card price format (€45,00).
    const money = (n) => '€' + Number(n || 0).toFixed(2).replace('.', ',');
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

    const cards = this.modules.map((mod) => {
      const size   = sizesById[mod.sizeId] || {};
      const finish = cfg.finishes.find(f => f.id === mod.finishId) || cfg.finishes[0];
      const color  = (finish.colors || [])[mod.colorIdx] || (finish.colors || [])[0] || {};
      const shape  = cfg.shapes.find(s => s.id === mod.shapeId) || (cfg.shapes || [])[0] || {};
      const key    = (shape.id || '') + '/' + mod.sizeId + '/' + (color.id || '');
      const vm     = (finish.variantMap && finish.variantMap[key]) || {};
      const price  = vm.price ?? ((size.price || 0) + (finish.priceModifier || 0) + (shape.priceModifier || 0));
      return {
        image: vm.image || '',
        night: vm.night || '',
        url:   vm.url   || '',
        title: finish.productTitle || finish.name || '',
        variant: (size.name || mod.sizeId || '') + ' · ' + (color.name || ''),
        price,
        swatch: color.swatch || color.ext || '#cccccc'
      };
    });

    // Reuse the homepage MONO card markup / classes verbatim so the
    // cards are visually identical (.mono-variants__* CSS is global via
    // the section {% stylesheet %}). Two same-src images stand in for
    // the day/night pair so the card stays visible in either theme on
    // the configurator page (which still allows dark mode); when only
    // a swatch colour is known, fall back to a filled media block.
    host.innerHTML = cards.map((c) => {
      const media = c.image
        ? `<img class="mono-variants__img mono-variants__img--day" src="${esc(c.image)}" alt="${esc(c.title + ' ' + c.variant)}" loading="lazy">
           <img class="mono-variants__img mono-variants__img--night" src="${esc(c.night || c.image)}" alt="" loading="lazy">`
        : `<span class="mono-variants__img mono-variants__img--day" style="background:${esc(c.swatch)}"></span>`;
      const inner = `
        <div class="mono-variants__media">${media}</div>
        <div class="mono-variants__info">
          <p class="mono-variants__variant">${esc(c.variant)}</p>
          <div class="mono-variants__info-row">
            <h3 class="mono-variants__title">${esc(c.title)}</h3>
            <p class="mono-variants__price"><span>${money(c.price)}</span></p>
          </div>
        </div>`;
      return `<li class="mono-variants__item">${
        c.url
          ? `<a class="mono-variants__card" href="${esc(c.url)}" aria-label="${esc(c.title + ' ' + c.variant)}">${inner}</a>`
          : `<div class="mono-variants__card">${inner}</div>`
      }</li>`;
    }).join('');

    // Cards in the below-the-fold section now open the modal instead of
    // navigating away (the strip in the viewer is the primary surface).
    host.querySelectorAll('.mono-variants__card').forEach((el, i) => {
      el.addEventListener('click', (e) => { e.preventDefault(); this.openModuleModal(i); });
    });

    const section = host.closest('[data-cfg-summary-section]');
    if (section) section.classList.toggle('is-empty', cards.length === 0);

    // Keep the compact in-viewer strip in sync + cache the card data
    // for the detail modal.
    this._buildCards = cards;
    this.renderBuildStrip(cards);
  }

  // "Your build" chips in the bottom bar — one photo chip per module
  // (module idx aligned with `cards`) plus a "+" chip to add another.
  // Hover shows an eye (detail) + a small trash; clicking the chip
  // opens the product-detail modal, the trash removes the module.
  renderBuildStrip (cards) {
    const strip = this.root.querySelector('[data-cfg-buildstrip]');
    if (!strip) return;
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const canAdd = this.canAddModule();
    const eyeSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z"/><circle cx="12" cy="12" r="3"/></svg>`;
    const trashSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>`;
    // "Use as" add-on chips — Light / Vase are future modules; show
    // them in the build so the customer sees everything the order
    // will contain (price rides on the toggle / total).
    const bulbSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.4 1 2.3h6c0-.9.4-1.8 1-2.3A7 7 0 0 0 12 2z"/></svg>`;
    const vaseSvg = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2h8c0 3-1 4-1 6 0 2 2 3 2 7a5 5 0 0 1-5 5h0a5 5 0 0 1-5-5c0-4 2-5 2-7 0-2-1-3-1-6z"/></svg>`;
    const addonChip = (key, label, icon) => {
      if (!this.useAs?.[key]) return '';
      const p = this.useAsPrice(key);
      // Backed by a real product (vase = glass insert)? Then the chip
      // shows the product photo and its store name — same product as
      // on the Parts page, deduplicated.
      const prod = this.config.useAsProducts?.[key];
      const visual = prod?.image
        ? `<img src="${esc(prod.image)}" alt="" loading="lazy">`
        : `${icon}
            <span class="cfg__chip-addon-label">${label}</span>`;
      const name = key === 'vase' && prod ? 'Glass Vase' : label;
      return `
        <span class="cfg__chip-wrap cfg__chip-wrap--addon" title="${esc(name)}${p ? ' · +€' + p : ''}">
          <span class="cfg__chip cfg__chip--addon">
            ${visual}
          </span>
          <span class="cfg__chip-name" aria-hidden="true"><strong>${esc(name)}</strong>${p ? `<small>+€${p}</small>` : ''}</span>
        </span>`;
    };
    // FIXED chip order — by when the module was ADDED (persistent _uid),
    // not by its current position in the stack. Browsing combinations
    // reorders the lamp, not the chips.
    this.modules.forEach(m => {
      if (m._uid == null) m._uid = (this._uidSeq = (this._uidSeq || 0) + 1);
    });
    const displayOrder = this.modules
      .map((m, i) => ({ uid: m._uid, i }))
      .sort((a, b) => a.uid - b.uid)
      .map(o => o.i);
    strip.innerHTML = `
      ${displayOrder.map(i => { const c = cards[i]; if (!c) return '';
        const short = esc((c.title || '').split('·')[0].replace(/^LASE\s*/i, '').trim() || c.title);
        return `
        <span class="cfg__chip-wrap" data-cfg-row="${i}" title="${esc(c.title + ' · ' + c.variant)}">
          <button type="button" class="cfg__chip" data-cfg-chip="${i}" aria-label="${esc(c.title)}">
            ${c.image
              ? `<img src="${esc(c.image)}" alt="" loading="lazy">`
              : `<span class="cfg__chip-swatch" style="background:${esc(c.swatch)}"></span>`}
            <span class="cfg__chip-eye" aria-hidden="true">${eyeSvg}</span>
          </button>
          <span class="cfg__chip-name" aria-hidden="true"><strong>${short}</strong><small>${esc(c.variant)}</small></span>
          <button type="button" class="cfg__chip-trash" data-cfg-chip-remove="${i}" aria-label="Remove module" title="Remove module">${trashSvg}</button>
        </span>`; }).join('')}
      ${this.connectors.map((cn, i) => {
        const col = this.config.connectorColors[cn.colorIdx] || this.config.connectorColors[0];
        return `
        <span class="cfg__chip-wrap" data-cfg-row-conn="${i}" title="Connector · ${esc(col?.name || '')} · €${this.config.connectorPrice}">
          <button type="button" class="cfg__chip cfg__chip--connector" data-cfg-chip-conn="${i}">
            ${col?.image
              ? `<img src="${esc(col.image)}" alt="" loading="lazy">`
              : `<span class="cfg__chip-conn-dot" style="background:${esc(col?.ext || '#ddd')}"></span>
            <span class="cfg__chip-addon-label">Con</span>`}
            <span class="cfg__chip-eye" aria-hidden="true">${eyeSvg}</span>
          </button>
          <span class="cfg__chip-name" aria-hidden="true"><strong>Connector</strong><small>${esc(col?.name || '')}</small></span>
        </span>`;
      }).join('')}
      ${cards.length ? addonChip('light', 'Light', bulbSvg) + addonChip('vase', 'Vase', vaseSvg) : ''}
      <button type="button" class="cfg__chip cfg__chip--add${canAdd ? '' : ' is-disabled'}"
        data-cfg-chip-add ${canAdd ? '' : `disabled title="Max height reached (${this.config.maxHeightMm} mm)"`}
        aria-label="Add module" ${canAdd ? 'title="Add module"' : ''}>+<span class="cfg__chip-add-label">Add module</span></button>`;
    // The whole ROW is the click target (desktop rows / mobile chips
    // alike) — the trash stops propagation below, so anything else on
    // the row opens (or, if already open for that item, closes) the
    // detail modal.
    strip.querySelectorAll('[data-cfg-row]').forEach(w => {
      w.addEventListener('click', () => this.openModuleModal(+w.dataset.cfgRow));
    });
    strip.querySelectorAll('[data-cfg-row-conn]').forEach(w => {
      w.addEventListener('click', () => this.openConnectorModal(+w.dataset.cfgRowConn));
    });
    strip.querySelectorAll('[data-cfg-chip-remove]').forEach(b => {
      b.addEventListener('click', (e) => {
        e.stopPropagation();
        this.removeModule(+b.dataset.cfgChipRemove);
      });
    });
    const add = strip.querySelector('[data-cfg-chip-add]');
    if (add) add.addEventListener('click', () => this.addModule());
    this.fitBuildList();
  }

  // One-line explanation of what the current Light/Vase selection
  // means in real use — so nobody orders a build they can't use the
  // way they imagined (no vase insert / no light kit).
  renderUseNote () {
    const el = this.root.querySelector('[data-cfg-use-note]');
    if (!el) return;
    el.classList.remove('is-warn');
    const light = !!this.useAs?.light;
    const vase  = !!this.useAs?.vase;
    el.textContent = light && vase
      ? 'A vase by day, a lamp by night.'
      : light
        ? 'Lamp only: comes without the vase insert.'
        : 'Vase only: comes without the light kit, it will not glow.';
  }

  flashUseNote (msg) {
    const el = this.root.querySelector('[data-cfg-use-note]');
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-warn');
    clearTimeout(this._useNoteT);
    this._useNoteT = setTimeout(() => this.renderUseNote(), 2400);
  }

  // Desktop rail: the build list gets the space between "Use as" and
  // the sticky bottom group (Dimensions · Combinations · CTA). Rows
  // that don't fit fold behind a "Show all (+N)" row; expanding
  // scrolls INSIDE the list only — the CTA never leaves the screen.
  fitBuildList () {
    const strip = this.root.querySelector('[data-cfg-buildstrip]');
    if (!strip) return;
    strip.querySelector('[data-cfg-showall]')?.remove();
    const rows = [...strip.children];
    rows.forEach(r => r.classList.remove('is-overflow'));
    const desktop = window.matchMedia('(min-width: 900px)').matches;
    strip.classList.toggle('is-expanded', desktop && !!this._buildListExpanded);
    if (!desktop) return;
    const fits = () => strip.scrollHeight <= strip.clientHeight + 1;
    if (fits()) { this._buildListExpanded = false; strip.classList.remove('is-expanded'); return; }
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'cfg__chip-showall';
    btn.dataset.cfgShowall = '1';
    strip.appendChild(btn);
    if (this._buildListExpanded) {
      btn.textContent = 'Show less';
      btn.addEventListener('click', () => { this._buildListExpanded = false; this.fitBuildList(); });
      return;
    }
    let hidden = 0;
    for (let i = rows.length - 1; i >= 0 && !fits(); i--) {
      rows[i].classList.add('is-overflow');
      hidden++;
    }
    btn.textContent = `Show all (+${hidden})`;
    btn.addEventListener('click', () => { this._buildListExpanded = true; this.fitBuildList(); });
  }

  // Any modal opening folds the mobile bar back — never two expanded
  // layers on screen at once.
  collapseBar () {
    const bar = this.root.querySelector('.cfg__bar');
    if (bar) bar.classList.remove('is-open');
    this.root.querySelector('[data-cfg-bar-toggle]')?.setAttribute('aria-expanded', 'false');
  }

  // Modal media block. With BOTH a day and a night photo it becomes a
  // horizontal snap-scroll gallery (day | night — same pairing as the
  // part PDP hero); with one photo it stays a single image; with none,
  // a flat colour swatch.
  modalMediaHTML (image, night, swatch) {
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    if (image && night) {
      return `
        <div class="cfg__modal-media cfg__modal-media--gallery" data-cfg-modal-gallery>
          <div class="cfg__modal-slide"><img src="${esc(image)}" alt="" loading="lazy"></div>
          <div class="cfg__modal-slide"><img src="${esc(night)}" alt="" loading="lazy"></div>
        </div>
        <div class="cfg__modal-dots" data-cfg-modal-dots aria-hidden="true">
          <span class="is-active"></span><span></span>
        </div>`;
    }
    if (image) return `<div class="cfg__modal-media"><img src="${esc(image)}" alt=""></div>`;
    return `<div class="cfg__modal-media"><span style="display:block;width:100%;height:100%;background:${esc(swatch || '#ddd')}"></span></div>`;
  }

  // Keep the gallery dots in sync with the scroll position.
  bindModalGallery (body) {
    const gal  = body.querySelector('[data-cfg-modal-gallery]');
    const dots = body.querySelector('[data-cfg-modal-dots]');
    if (!gal || !dots) return;
    gal.addEventListener('scroll', () => {
      const idx = Math.round(gal.scrollLeft / gal.clientWidth);
      dots.querySelectorAll('span').forEach((d, n) => d.classList.toggle('is-active', n === idx));
    }, { passive: true });
  }

  // Connector detail — same modal shell as modules. Media = day|night
  // photo gallery when the colour variant has both; the connector IS a
  // real cart product (Konektor variants per colour, €15).
  openConnectorModal (i) {
    const modal = this.root.querySelector('[data-cfg-modal]');
    const body  = this.root.querySelector('[data-cfg-modal-body]');
    const cn = this.connectors[i];
    if (!modal || !body || !cn) return;
    // Second click on the same row toggles the detail closed.
    if (this._modalKey === 'con:' + i && modal.classList.contains('is-open')) { this.closeModal(); return; }
    this._modalKey = 'con:' + i;
    this.collapseBar();
    const col = this.config.connectorColors[cn.colorIdx] || this.config.connectorColors[0];
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const money = (n) => '€' + Number(n || 0).toFixed(2).replace('.', ',');
    body.innerHTML = `
      ${this.modalMediaHTML(col?.image, col?.night, col?.ext)}
      <p class="cfg__modal-variant">${esc(col?.name || '')}</p>
      <h3 class="cfg__modal-title">Connector</h3>
      <p class="cfg__modal-price">${money(this.config.connectorPrice)}</p>
      <p class="cfg__modal-note">The flexible joint that locks two modules together, in the colour you pick. Click the connector in the 3D view to change its colour.</p>`;
    this.bindModalGallery(body);
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => modal.classList.add('is-open'));
  }

  openModuleModal (i) {
    const modal = this.root.querySelector('[data-cfg-modal]');
    const body  = this.root.querySelector('[data-cfg-modal-body]');
    const c = (this._buildCards || [])[i];
    if (!modal || !body || !c) return;
    // Second click on the same row toggles the detail closed.
    if (this._modalKey === 'mod:' + i && modal.classList.contains('is-open')) { this.closeModal(); return; }
    this._modalKey = 'mod:' + i;
    this.collapseBar();
    const esc = (s) => String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const money = (n) => '€' + Number(n || 0).toFixed(2).replace('.', ',');
    body.innerHTML = `
      ${this.modalMediaHTML(c.image, c.night, c.swatch)}
      <p class="cfg__modal-variant">${esc(c.variant)}</p>
      <h3 class="cfg__modal-title">${esc(c.title)}</h3>
      <p class="cfg__modal-price">${money(c.price)}</p>`;
    this.bindModalGallery(body);
    modal.removeAttribute('hidden');
    modal.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => modal.classList.add('is-open'));
  }

  closeModal () {
    const modal = this.root.querySelector('[data-cfg-modal]');
    if (!modal) return;
    this._modalKey = null;
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    setTimeout(() => modal.setAttribute('hidden', ''), 280);
  }

  buildModuleCard (idx) {
    const mod    = this.modules[idx];
    const active = this.isActive('module', idx);
    const finish = this.config.finishes.find(f => f.id === mod.finishId) || this.config.finishes[0];
    const activeColor = finish.colors[mod.colorIdx] || finish.colors[0];
    // Removal is never blocked by the build rule now — you can freely
    // edit down to an invalid state; the Add-to-cart button is what
    // enforces validity (refreshCartValidity). Always allow removal
    // (even the last module — "start from scratch" wants an empty stack).
    const canRemove = true;

    const item = document.createElement('div');
    item.className = 'cfg-slot cfg-slot--module' + (active ? ' is-open' : '');
    item.dataset.kind = 'module';
    item.dataset.moduleIdx = String(idx);

    const modPrice = this.modulePrice(idx);

    if (!active) {
      // Collapsed card — header + a compact summary stripe.
      // Layout: [drag] [Module] [chevron] ......... [trash]
      // Summary: [swatch] size·finish·color ......... €NN
      item.innerHTML = `
        <div class="cfg-slot__header" data-action="focus">
          <span class="cfg-slot__handle" data-action="drag" aria-label="Drag" title="Drag to reorder">⋮⋮</span>
          <span class="cfg-slot__name">Module</span>
          <span class="cfg-slot__chevron" aria-hidden="true">${this.iconChevron()}</span>
          ${this.moduleTrashBtn(canRemove)}
        </div>
        <div class="cfg-slot__summary" data-action="focus">
          <span class="cfg-slot__summary-swatch" style="--swatch:${activeColor.swatch || activeColor.ext};"></span>
          <span>${mod.sizeId} · ${finish.name} · ${activeColor.name}</span>
          <span class="cfg-slot__summary-price">€${modPrice}</span>
        </div>
      `;
      return item;
    }

    // Expanded card.
    // Disable size pills that would push the stack over maxHeightMm —
    // the user can't pick L if the current build can't accommodate
    // the extra height.
    const sizes = this.config.sizes.map(s => {
      const isActive  = s.id === mod.sizeId;
      // Only the height cap disables a size pill now. The "needs an M/L
      // body" rule is enforced at Add-to-cart, not by locking S — so
      // the user can freely try an all-S stack and just gets told they
      // can't check out yet.
      const fits = isActive || this.wouldFitSize(idx, s.id);
      // Price the pill for the module's CURRENT finish + colour so
      // switching size never surprises (Glow L ≠ Matte L).
      const pillPrice = this.partPrice(s.id, mod.shapeId, mod.finishId, mod.colorIdx);
      return `
        <button type="button"
          class="cfg-variant${isActive ? ' is-active' : ''}${fits ? '' : ' is-pending'}"
          data-action="set-size" data-size-id="${s.id}"
          ${fits ? '' : 'disabled title="Would exceed max stack height"'}>
          <span class="cfg-variant__name">${s.name}</span>
          <span class="cfg-variant__price">€${pillPrice}</span>
        </button>
      `;
    }).join('');

    // (The Shape picker is intentionally NOT rendered — a single shape
    // exists for now; the row returns when a second pattern ships.)

    // Colour picker — photo thumbs when the exact (shape × size ×
    // colour) variant has a product photo in the variantMap, so the
    // customer sees the REAL part they're configuring (photo follows
    // the currently selected size/shape/finish). Hex dot fallback for
    // colours without a photographed variant.
    const swatches = finish.colors.map((c, ci) => {
      const vKey = (mod.shapeId || '') + '/' + mod.sizeId + '/' + (c.id || '');
      const vm   = (finish.variantMap && finish.variantMap[vKey]) || {};
      const img  = vm.image ? String(vm.image).replace(/width=\d+/, 'width=240') : '';
      const media = img
        ? `<img class="cfg-swatch__img" src="${img}" alt="" loading="lazy">`
        : `<span class="cfg-swatch__dot"></span>`;
      return `
      <button type="button"
        class="cfg-swatch${img ? ' cfg-swatch--photo' : ''}${ci === mod.colorIdx ? ' is-active' : ''}"
        data-action="set-color" data-color-idx="${ci}"
        style="--swatch: ${c.swatch || c.ext};"
        aria-label="${c.name}" title="${c.name}">
        ${media}
      </button>
    `;
    }).join('');

    // Quiet header — name + trash. Reorder: desktop uses the floating
    // arrow/drag column left of the card; on MOBILE (bottom sheet) the
    // ↑/↓ pair lives right here in the header (CSS toggles which shows).
    const canUp   = idx < this.modules.length - 1;
    const canDown = idx > 0;
    item.innerHTML = `
      <div class="cfg-slot__header">
        <span class="cfg-slot__name">Module</span>
        <span class="cfg-slot__order">
          <button type="button" class="cfg-slot__icon" data-action="move-up" ${canUp ? '' : 'disabled'} aria-label="Move up" title="Move up">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
          </button>
          <button type="button" class="cfg-slot__icon" data-action="move-down" ${canDown ? '' : 'disabled'} aria-label="Move down" title="Move down">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><polyline points="19 12 12 19 5 12"/></svg>
          </button>
        </span>
        ${this.moduleTrashBtn(canRemove)}
      </div>

      <p class="cfg-slot__sublabel">Size</p>
      <div class="cfg-slot__variants">${sizes}</div>

      <p class="cfg-slot__sublabel">Finish</p>
      <div class="cfg-finish">
        ${this.config.finishes.map(f => `
          <button type="button"
            class="cfg-finish__btn${f.id === mod.finishId ? ' is-active' : ''}"
            data-action="set-finish" data-finish-id="${f.id}"
            title="${f.hint}">
            ${this.finishIcon(f.id)}
            <span class="cfg-finish__name">${f.name}</span>
            ${f.priceModifier ? `<span class="cfg-finish__price">+€${f.priceModifier}</span>` : ''}
          </button>
        `).join('')}
      </div>

      <div class="cfg-slot__color">
        <p class="cfg-slot__color-label">Color: <span class="cfg-slot__color-name">${activeColor.name}</span></p>
        <div class="cfg-slot__colors">${swatches}</div>
      </div>

      <div class="cfg-slot__total">
        <span class="cfg-slot__total-label">Module total</span>
        <span class="cfg-slot__total-amount">€${modPrice}</span>
      </div>
    `;
    return item;
  }

  // ---- Inline SVG icon helpers for the slot cards --------------
  iconTrash () {
    return `<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/></svg>`;
  }
  iconChevron () {
    // Decorative chevron — rotates to chevron-up via CSS when .is-open
    return `<svg class="cfg-slot__chevron-svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>`;
  }

  // Trash button — pinned to the right edge of the module header.
  // Reorder happens via the 3D overlay arrows now, not buttons in the
  // card, so the header is much quieter.
  moduleTrashBtn (canRemove) {
    return `
      <button type="button" class="cfg-slot__icon cfg-slot__icon--danger cfg-slot__trash"
        data-action="remove" ${canRemove ? '' : 'disabled'}
        aria-label="Delete module" title="Delete module">
        ${this.iconTrash()}
      </button>
    `;
  }

  finishIcon (finishId) {
    if (finishId === 'glow') {
      // Light bulb
      return `
        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M9 18h6"/>
          <path d="M10 21h4"/>
          <path d="M12 3a6 6 0 0 1 4 10.5c-.7.7-1 1.5-1 2.5v.5H9V16c0-1-.3-1.8-1-2.5A6 6 0 0 1 12 3z"/>
        </svg>
      `;
    }
    // Matte = filled disc
    return `
      <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
        <circle cx="12" cy="12" r="6"/>
      </svg>
    `;
  }

  buildConnectorCard (idx) {
    const conn = this.connectors[idx];
    const active = this.isActive('connector', idx);
    const activeColor = this.config.connectorColors[conn.colorIdx] || this.config.connectorColors[0];

    const item = document.createElement('div');
    item.className = 'cfg-slot cfg-slot--connector' + (active ? ' is-open' : '');
    item.dataset.kind = 'connector';
    item.dataset.connectorIdx = String(idx);

    const connPrice = this.config.connectorPrice || 0;

    if (!active) {
      // Layout: [swatch] Connector [chevron] ........... €15
      // The swatch acts as the left-edge "identity" element here —
      // the same role the drag handle plays in module cards. That
      // visually aligns the "Connector" text with the "Module" text
      // in the cards above + below, and lets the price end at the
      // far right of the row like module summary prices do.
      item.innerHTML = `
        <div class="cfg-slot__header" data-action="focus">
          <span class="cfg-slot__summary-swatch cfg-slot__summary-swatch--inline" style="--swatch:${activeColor.ext};"></span>
          <span class="cfg-slot__name">Connector</span>
          <span class="cfg-slot__summary-price cfg-slot__summary-price--inline">€${connPrice}</span>
        </div>
      `;
      return item;
    }

    const swatches = this.config.connectorColors.map((c, ci) => `
      <button type="button"
        class="cfg-swatch${ci === conn.colorIdx ? ' is-active' : ''}"
        data-action="set-connector-color" data-color-idx="${ci}"
        style="--swatch: ${c.ext};"
        aria-label="${c.name}" title="${c.name}">
        <span class="cfg-swatch__dot"></span>
      </button>
    `).join('');

    item.innerHTML = `
      <div class="cfg-slot__header">
        <span class="cfg-slot__name">Connector</span>
      </div>
      <div class="cfg-slot__color">
        <p class="cfg-slot__color-label">Color: <span class="cfg-slot__color-name">${activeColor.name}</span></p>
        <div class="cfg-slot__colors">${swatches}</div>
      </div>

      <div class="cfg-slot__total">
        <span class="cfg-slot__total-label">Price</span>
        <span class="cfg-slot__total-amount">€${connPrice}</span>
      </div>
    `;
    return item;
  }

  renderLightSection () {
    const wrap = this.root.querySelector('[data-cfg-light]');
    if (!wrap) return;
    const hasGlow = this.modules.some(m => m.finishId === 'glow');
    const cards = this.config.lightAddons.map(a => {
      const locked = !a.ready || (a.needsGlow && !hasGlow);
      return `
        <button type="button"
          class="cfg-light-card${this.lightAddonId === a.id ? ' is-active' : ''}${locked ? ' is-locked' : ''}"
          data-light-id="${a.id}"
          ${locked ? 'disabled' : ''}
          title="${a.ready ? '' : 'Coming soon'}">
          <span class="cfg-light-card__name">${a.name}</span>
          <span class="cfg-light-card__price">${a.price ? '+€' + a.price : 'Free'}</span>
          ${!a.ready ? '<span class="cfg-light-card__badge">Soon</span>' : ''}
        </button>
      `;
    }).join('');
    const constraintNotice = !hasGlow ? `
      <p class="cfg-light__notice">
        Light addons need at least one <strong>Glow</strong> module. Switch a module's finish to Glow to unlock.
      </p>
    ` : '';
    wrap.innerHTML = `
      <p class="cfg__section-title">Light</p>
      ${constraintNotice}
      <div class="cfg-light">${cards}</div>
    `;
  }

  refreshSortable () {
    if (this._sortable) { this._sortable.destroy(); this._sortable = null; }
    if (!window.Sortable) {
      setTimeout(() => this.refreshSortable(), 120);
      return;
    }
    this._sortable = window.Sortable.create(this.slotList, {
      handle:    '.cfg-slot__handle',
      draggable: '.cfg-slot--module',
      animation: 220,
      onEnd: () => {
        // Capture the active module's *reference* before we mutate the
        // array so we can re-find it after the reorder. Without this,
        // activeSlot.idx still points at the old slot position, which
        // is now occupied by a different module — the user sees their
        // card collapse and a different module's card pop open.
        const wasActiveModule =
          (this.activeSlot && this.activeSlot.kind === 'module')
            ? this.modules[this.activeSlot.idx]
            : null;

        const orderTop = Array.from(this.slotList.querySelectorAll('.cfg-slot--module'))
          .map(el => parseInt(el.dataset.moduleIdx, 10));
        const newModules = orderTop.slice().reverse().map(oldIdx => this.modules[oldIdx]);
        this.modules = newModules;

        // Re-pin the active card to whichever new slot now holds the
        // module the user was editing. Same behaviour as moveModule(),
        // so drag-reorder feels identical to using the 3D arrows.
        if (wasActiveModule) {
          const newIdx = newModules.indexOf(wasActiveModule);
          if (newIdx >= 0) {
            this.activeSlot = { kind: 'module', idx: newIdx };
          }
        }

        this.renderStack();
        this.rebuildScene();
        this.syncStateToUrl();
      }
    });
  }

  // Shared delegated handler for card actions — used by both the
  // guide stack (slotList) and the floating in-viewport editor.
  handleSlotAction (e) {
    const btn = e.target.closest('[data-action]');
    if (!btn || btn.disabled) return;
    const action = btn.dataset.action;

    if (action === 'add-module') { this.addModule(); return; }
    if (action === 'drag')       return;
    const slotEl = btn.closest('.cfg-slot');
    if (!slotEl) return;

    const kind = slotEl.dataset.kind;
    const idx = parseInt(slotEl.dataset[kind === 'module' ? 'moduleIdx' : 'connectorIdx'], 10);

    if (action === 'focus') {
      // The × in the floating editor header — closes the card
      // (toggling the already-active slot clears the selection).
      this.focusSlot(kind, idx, /* toggle */ true);
      return;
    }

    if (kind === 'module') {
      if (action === 'set-size')   this.setSize(idx, btn.dataset.sizeId);
      if (action === 'set-shape')  this.setShape(idx, btn.dataset.shapeId);
      if (action === 'set-finish') this.setFinish(idx, btn.dataset.finishId);
      if (action === 'set-color')  this.setModuleColor(idx, parseInt(btn.dataset.colorIdx, 10));
      if (action === 'remove')     this.removeModule(idx);
      if (action === 'move-up')    this.moveModule(idx, +1);
      if (action === 'move-down')  this.moveModule(idx, -1);
      if (action === 'details')    this.openModuleModal(idx);
    } else if (kind === 'connector') {
      if (action === 'set-connector-color') {
        this.setConnectorColor(idx, parseInt(btn.dataset.colorIdx, 10));
      }
    }
  }

  wireControls () {
    this.slotList.addEventListener('click', e => this.handleSlotAction(e));
    const floatEl = this.root.querySelector('[data-cfg-float]');
    if (floatEl) floatEl.addEventListener('click', e => this.handleSlotAction(e));

    // Use-as toggles (Light / Vase) — flags initialised in the
    // constructor; toggling refreshes the build chips + total.
    this.root.querySelectorAll('[data-cfg-use]').forEach(btn => {
      const price = this.useAsPrice(btn.dataset.cfgUse);
      if (price > 0) {
        const tag = document.createElement('span');
        tag.className = 'cfg__use-price';
        tag.textContent = `+€${price}`;
        btn.appendChild(tag);
      }
      btn.addEventListener('click', () => {
        const key = btn.dataset.cfgUse;
        const other = key === 'light' ? 'vase' : 'light';
        // At least ONE use stays on — a build that is neither a lamp
        // nor a vase would be unusable; block turning off the last one.
        if (this.useAs[key] && !this.useAs[other]) {
          this.flashUseNote('At least one is required: Light or Vase.');
          return;
        }
        this.useAs[key] = !this.useAs[key];
        btn.classList.toggle('is-on', this.useAs[key]);
        this.renderUseNote();
        this.renderBuildStrip(this._buildCards || []);
        this.tickPrice();
      });
    });
    this.renderUseNote();

    if (this.modeList) {
      this.modeList.addEventListener('click', e => {
        const btn = e.target.closest('[data-view-mode]');
        if (!btn) return;
        this.setViewMode(btn.dataset.viewMode);
      });
    }

    // 3D overlay reorder arrows — buttons live in the viewer but
    // share the same moveModule action as the (now-removed) card
    // chevrons. Tracked + repositioned each frame in updateOverlayArrows.
    this.overlayArrows = this.root.querySelector('[data-cfg-overlay-arrows]');
    if (this.overlayArrows) {
      this.overlayArrows.addEventListener('click', e => {
        const btn = e.target.closest('[data-action]');
        if (!btn || btn.disabled) return;
        // The drag handle is also a button — its primary interaction
        // is pointerdown→drag, not click. Skip click handling for it
        // so a click-with-no-drag doesn't trigger a move.
        if (btn.dataset.action === 'overlay-drag') return;
        const { kind, idx } = this.activeSlot || {};
        if (kind !== 'module' || idx == null) return;
        if (btn.dataset.action === 'overlay-move-up')   this.moveModule(idx, +1);
        if (btn.dataset.action === 'overlay-move-down') this.moveModule(idx, -1);
      });

      // Drag-to-reorder handle. Each ratchetThreshold pixels of
      // pointer movement triggers one moveModule(±1) step. The
      // active slot follows the moved module (moveModule updates
      // activeSlot.idx), so subsequent ratchets continue from the
      // new position automatically. Orbit controls are disabled
      // while a drag is in flight so the camera doesn't swing
      // when the user just wants to reorder.
      const dragHandle = this.overlayArrows.querySelector('[data-action="overlay-drag"]');
      if (dragHandle) {
        const ratchetThreshold = 40; // px

        dragHandle.addEventListener('pointerdown', (e) => {
          if (e.button !== 0 && e.pointerType === 'mouse') return;
          const { kind, idx } = this.activeSlot || {};
          if (kind !== 'module' || idx == null) return;
          dragHandle.setPointerCapture(e.pointerId);
          this._overlayDrag = {
            pointerId:    e.pointerId,
            lastTriggerY: e.clientY
          };
          this.overlayArrows.classList.add('is-dragging');
          if (this.controls) this.controls.enabled = false;
          e.preventDefault();
          e.stopPropagation();
        });

        dragHandle.addEventListener('pointermove', (e) => {
          if (!this._overlayDrag || e.pointerId !== this._overlayDrag.pointerId) return;
          const { kind, idx } = this.activeSlot || {};
          if (kind !== 'module' || idx == null) return;
          const delta = e.clientY - this._overlayDrag.lastTriggerY;
          if (delta <= -ratchetThreshold) {
            // Pointer moved UP on screen → stack TOP → higher idx
            this.moveModule(idx, +1);
            this._overlayDrag.lastTriggerY = e.clientY;
          } else if (delta >= ratchetThreshold) {
            // Pointer moved DOWN on screen → stack BOTTOM → lower idx
            this.moveModule(idx, -1);
            this._overlayDrag.lastTriggerY = e.clientY;
          }
        });

        const endDrag = (e) => {
          if (!this._overlayDrag || e.pointerId !== this._overlayDrag.pointerId) return;
          try { dragHandle.releasePointerCapture(this._overlayDrag.pointerId); } catch (_e) {}
          this._overlayDrag = null;
          this.overlayArrows.classList.remove('is-dragging');
          if (this.controls) this.controls.enabled = true;
        };
        dragHandle.addEventListener('pointerup',     endDrag);
        dragHandle.addEventListener('pointercancel', endDrag);
        // Safety nets: if pointer capture is lost for any reason
        // (the captured element gets removed from the DOM mid-drag
        // when the overlay rebuilds, the OS preempts the gesture,
        // the user releases outside the canvas with capture already
        // gone) the element-level pointerup never fires — and the
        // camera stays frozen because controls.enabled is still
        // false. Listening at the window level + on lostpointercapture
        // guarantees endDrag always runs exactly once for every
        // pointerdown. endDrag is idempotent (guards on _overlayDrag
        // + pointerId match) so duplicate triggers are harmless.
        dragHandle.addEventListener('lostpointercapture', endDrag);
        window.addEventListener('pointerup',     endDrag);
        window.addEventListener('pointercancel', endDrag);
      }
    }

    const lightWrap = this.root.querySelector('[data-cfg-light]');
    if (lightWrap) {
      lightWrap.addEventListener('click', e => {
        const card = e.target.closest('.cfg-light-card');
        if (!card || card.disabled) return;
        this.lightAddonId = card.dataset.lightId;
        this.renderLightSection();
        this.tickPrice();
        this.renderDimensionsPanel();
        this.syncStateToUrl();
      });
    }

    const resetBtn = this.root.querySelector('[data-cfg-reset]');
    if (resetBtn) resetBtn.addEventListener('click', () => this.fitCameraToStack());

    const rndBtn = this.root.querySelector('[data-cfg-randomize]');
    if (rndBtn) rndBtn.addEventListener('click', () => this.randomize());

    const addTop = this.root.querySelector('[data-cfg-add-top]');
    if (addTop) addTop.addEventListener('click', () => this.addModule('top'));
    const addBot = this.root.querySelector('[data-cfg-add-bottom]');
    if (addBot) addBot.addEventListener('click', () => this.addModule('bottom'));

    const arrPrev = this.root.querySelector('[data-cfg-arr-prev]');
    if (arrPrev) arrPrev.addEventListener('click', () => this.applyArrangement((this._arrIdx ?? 0) - 1));
    const arrNext = this.root.querySelector('[data-cfg-arr-next]');
    if (arrNext) arrNext.addEventListener('click', () => this.applyArrangement((this._arrIdx ?? 0) + 1));
    const arrSegs = this.root.querySelector('[data-cfg-arr-segs]');
    if (arrSegs) arrSegs.addEventListener('click', (e) => {
      const s = e.target.closest('[data-arr-seg]');
      if (s) this.applyArrangement(+s.dataset.arrSeg);
    });
    const arrDetail = this.root.querySelector('[data-cfg-arr-detail]');
    if (arrDetail) arrDetail.addEventListener('click', () => this.openArrangementsModal());
    const combiOpen = this.root.querySelector('[data-cfg-combi-open]');
    if (combiOpen) combiOpen.addEventListener('click', () => this.openArrangementsModal());

    const emptyAdd = this.root.querySelector('[data-cfg-empty-add]');
    if (emptyAdd) emptyAdd.addEventListener('click', () => this.addModule());

    // Mobile bar expand/collapse.
    const barToggle = this.root.querySelector('[data-cfg-bar-toggle]');
    if (barToggle) barToggle.addEventListener('click', () => {
      const bar = this.root.querySelector('.cfg__bar');
      const open = bar.classList.toggle('is-open');
      barToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    // Mobile sheet: swipe DOWN on the grab zone to close the editor.
    // While dragging, the 3D viewer grows LINEARLY with the sheet's
    // travel (margin-bottom shrinks -> ResizeObserver re-fits the
    // camera each step). The stage stays clean the whole time - the
    // toolbar/combinations only come back once the sheet fully closes
    // (renderFloatCard's hide path drops the cfg--sheet class).
    const grab = this.root.querySelector('[data-cfg-float-grab]');
    const sheetEl = this.root.querySelector('[data-cfg-float]');
    if (grab && sheetEl) {
      let drag = null;
      grab.addEventListener('pointerdown', (e) => {
        const viewer = this.canvas?.parentElement;
        const bar = this.root.querySelector('.cfg__bar');
        drag = {
          startY: e.clientY,
          id: e.pointerId,
          viewer,
          reserve: Math.max(0, sheetEl.offsetHeight - (bar ? bar.offsetHeight : 0))
        };
        sheetEl.style.transition = 'none';
        try { grab.setPointerCapture(e.pointerId); } catch (err) {}
        e.preventDefault();
      });
      grab.addEventListener('pointermove', (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dy = Math.max(0, e.clientY - drag.startY);
        sheetEl.style.transform = `translateY(${dy}px)`;
        if (drag.viewer) {
          drag.viewer.style.marginBottom = Math.max(0, drag.reserve - dy) + 'px';
        }
      });
      const endDrag = (e) => {
        if (!drag || e.pointerId !== drag.id) return;
        const dy = e.clientY - drag.startY;
        const d = drag;
        drag = null;
        if (dy > 90) {
          // Far enough - close (same path as tapping empty space).
          sheetEl.style.transform = '';
          sheetEl.style.transition = '';
          const { kind, idx } = this.activeSlot || {};
          if (kind != null) this.focusSlot(kind, idx, /* toggle */ true);
        } else {
          // Snap back - sheet and viewer reservation return together.
          sheetEl.style.transition = 'transform 200ms ease';
          sheetEl.style.transform = '';
          if (d.viewer) d.viewer.style.marginBottom = d.reserve + 'px';
          setTimeout(() => { sheetEl.style.transition = ''; }, 220);
        }
      };
      grab.addEventListener('pointerup', endDrag);
      grab.addEventListener('pointercancel', endDrag);
    }

    this.wireSaveShare();

    const fsBtn = this.root.querySelector('[data-cfg-fullscreen]');
    if (fsBtn) fsBtn.addEventListener('click', () => this.toggleFullscreen());

    const dimsBtn = this.root.querySelector('[data-cfg-toggle-dims]');
    if (dimsBtn) dimsBtn.addEventListener('click', () => this.toggleDimsOverlay());

    const themeBtn = this.root.querySelector('[data-cfg-theme]');
    if (themeBtn) themeBtn.addEventListener('click', () => this.toggleTheme());

    this.root.querySelectorAll('[data-cfg-modal-close]').forEach(el =>
      el.addEventListener('click', () => this.closeModal()));
    window.addEventListener('keydown', e => {
      if (e.key === 'Escape') this.closeModal();
    });

    const cartBtn = this.root.querySelector('[data-cfg-add]');
    if (cartBtn) cartBtn.addEventListener('click', () => this.summariseConfiguration());

    window.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.fullscreen) this.toggleFullscreen();
    });
  }

  // ---- Save + Share ---------------------------------------------
  // The URL hash IS the design (encodeState), so sharing = sharing
  // the current URL. Save captures an email → Shopify customer
  // (form_type=customer) tagged saved-design.
  wireSaveShare () {
    const openDialog = (el) => {
      if (!el) return;
      el.removeAttribute('hidden');
      el.setAttribute('aria-hidden', 'false');
      requestAnimationFrame(() => el.classList.add('is-open'));
    };
    const closeDialog = (el) => {
      if (!el) return;
      el.classList.remove('is-open');
      el.setAttribute('aria-hidden', 'true');
      setTimeout(() => el.setAttribute('hidden', ''), 220);
    };
    const shareDlg = this.root.querySelector('[data-cfg-share-dialog]');
    const saveDlg  = this.root.querySelector('[data-cfg-save-dialog]');

    this.root.querySelectorAll('[data-cfg-dialog-close]').forEach(b =>
      b.addEventListener('click', () => { closeDialog(shareDlg); closeDialog(saveDlg); }));
    window.addEventListener('keydown', e => {
      if (e.key === 'Escape') { closeDialog(shareDlg); closeDialog(saveDlg); }
    });

    const shareBtn = this.root.querySelector('[data-cfg-share]');
    if (shareBtn) shareBtn.addEventListener('click', () => {
      // Surface the native option only where the device supports it.
      const nativeBtn = shareDlg?.querySelector('[data-cfg-share-native]');
      if (nativeBtn) nativeBtn.toggleAttribute('hidden', !navigator.share);
      openDialog(shareDlg);
    });
    const nativeBtn = this.root.querySelector('[data-cfg-share-native]');
    if (nativeBtn) nativeBtn.addEventListener('click', async () => {
      try {
        await navigator.share({
          title: 'My LASE design',
          text:  'Check out the lamp I built:',
          url:   location.href
        });
        closeDialog(shareDlg);
      } catch (e) { /* user dismissed the sheet */ }
    });
    const copyBtn = this.root.querySelector('[data-cfg-share-copy]');
    if (copyBtn) copyBtn.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(location.href);
        copyBtn.textContent = 'Copied ✓';
        setTimeout(() => { copyBtn.textContent = 'Copy link'; closeDialog(shareDlg); }, 1200);
      } catch (e) {
        copyBtn.textContent = 'Copy failed — copy the address bar';
      }
    });

    const saveBtn = this.root.querySelector('[data-cfg-save]');
    if (saveBtn) saveBtn.addEventListener('click', () => openDialog(saveDlg));
    const saveForm = this.root.querySelector('[data-cfg-save-form]');
    if (saveForm) saveForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const email = saveForm.querySelector('input[name="email"]')?.value?.trim();
      const note  = this.root.querySelector('[data-cfg-save-note]');
      if (!email) return;
      const submitBtn = saveForm.querySelector('button[type="submit"]');
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Saving…'; }
      try {
        // Shopify customer-capture form: creates the customer account
        // (visible in admin, tagged) without leaving the page.
        const body = new URLSearchParams({
          form_type: 'customer',
          utf8: '✓',
          'contact[email]': email,
          'contact[tags]': 'saved-design,configurator'
        });
        await fetch('/contact', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: body.toString()
        });
        try { await navigator.clipboard.writeText(location.href); } catch (e2) {}
        if (note) {
          note.textContent = 'Saved! Your LASE account is ready — and this design’s link is in your clipboard.';
          note.removeAttribute('hidden');
        }
        saveForm.setAttribute('hidden', '');
      } catch (err) {
        if (note) {
          note.textContent = 'Couldn’t save right now — please try again.';
          note.removeAttribute('hidden');
        }
        if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Save design'; }
      }
    });
  }

  // ============== State mutations ==============

  // Is the current stack buildable/orderable? Needs ≥2 modules and at
  // least one M/L body (S-only doesn't assemble). Returns {ok, reason}.
  buildValidity () {
    if (this.modules.length === 0)
      return { ok: false, reason: 'Add a module to start' };
    if (this.modules.length < 2)
      return { ok: false, reason: 'Add one more module (min. 2)' };
    if (!this.modules.some(m => m.sizeId !== 'S'))
      return { ok: false, reason: 'Add at least one M or L module' };
    return { ok: true, reason: '' };
  }

  // Enable/disable the Add-to-cart button per build validity and show
  // the reason inline. Called from renderStack (runs on every change).
  refreshCartValidity () {
    const btn = this.root.querySelector('[data-cfg-add]');
    if (!btn) return;
    const v = this.buildValidity();
    btn.disabled = !v.ok;
    btn.classList.toggle('is-invalid', !v.ok);
    let hint = this.root.querySelector('[data-cfg-cart-hint]');
    if (!v.ok) {
      if (!hint) {
        hint = document.createElement('p');
        hint.dataset.cfgCartHint = '1';
        hint.className = 'cfg__cart-hint';
        btn.parentElement.appendChild(hint);
      }
      hint.textContent = v.reason;
    } else if (hint) {
      hint.remove();
    }
  }

  // A build always needs at least one module larger than S — S-only
  // stacks don't assemble (S is a topper, not a base; even five S
  // modules won't work). True when the stack keeps an M/L body after
  // changing module `idx` to `newSizeId` (pass null to test removal).
  keepsBody (idx, newSizeId) {
    return this.modules.some((m, j) => {
      const size = j === idx ? newSizeId : m.sizeId;
      return size != null && size !== 'S';
    });
  }

  setSize (idx, sizeId) {
    if (!this.modules[idx]) return;
    // Refuse the change if it would push the compact-mode stack over
    // the configured maxHeightMm. The size pill is already rendered
    // as disabled in that case, but this is a runtime safety net for
    // hash-loaded / preset-loaded states that bypass the picker.
    if (!this.wouldFitSize(idx, sizeId)) return;
    this.modules[idx].sizeId = sizeId;
    this._popTargets = new Set([idx]);
    this.renderStack();
    // Auto-refit: size changes alter the stack height — re-frame so
    // the whole lamp always sits nicely in view (same as randomize).
    this.rebuildScene().then(() => this.fitCameraToStack());
    this.tickPrice();
    this.renderDimensionsPanel();
    this.nudgeIdle();
    this.syncStateToUrl();
  }

  setShape (idx, shapeId) {
    if (!this.modules[idx]) return;
    this.modules[idx].shapeId = shapeId;
    this.renderStack();
    this.tickPrice();
    this.renderDimensionsPanel();
    this.syncStateToUrl();
  }

  setFinish (idx, finishId) {
    if (!this.modules[idx]) return;
    const newFinish = this.config.finishes.find(f => f.id === finishId);
    if (!newFinish) return;
    // Keep the chosen colour across the finish switch — matte orange →
    // glow orange, not a reset to the first swatch. Palettes share
    // colour ids, so match by id and only fall back to 0 when the new
    // finish genuinely lacks the colour.
    const oldFinish = this.config.finishes.find(f => f.id === this.modules[idx].finishId);
    const oldColorId = oldFinish?.colors[this.modules[idx].colorIdx]?.id;
    const keepIdx = oldColorId ? newFinish.colors.findIndex(c => c.id === oldColorId) : -1;
    this.modules[idx].finishId = finishId;
    this.modules[idx].colorIdx = keepIdx >= 0 ? keepIdx : 0;
    this._popTargets = new Set([idx]);
    this.renderStack();
    this.renderLightSection();
    this.rebuildScene();
    this.tickPrice();
    this.renderDimensionsPanel();
    this.nudgeIdle();
    this.syncStateToUrl();
  }

  setModuleColor (idx, colorIdx) {
    if (!this.modules[idx]) return;
    this.modules[idx].colorIdx = colorIdx;
    this._popTargets = new Set([idx]);
    this.renderStack();
    this.rebuildScene();
    this.nudgeIdle();
    this.syncStateToUrl();
  }

  setConnectorColor (idx, colorIdx) {
    if (!this.connectors[idx]) return;
    this.connectors[idx].colorIdx = colorIdx;
    this.renderStack();
    this.rebuildScene();
    this.syncStateToUrl();
  }

  addModule (at = 'bottom') {
    if (!this.canAddModule()) return;
    // Any build-creating action closes the start guide — adding a
    // module from the bar chip while the guide was open used to leave
    // the guide floating over a live build.
    this.dismissGuide();
    // Pick the largest size that still fits in the height budget,
    // falling back to the smallest. Lets users keep adding modules
    // even when the default M no longer fits — the configurator
    // automatically downgrades to S without the user having to think
    // about it.
    const max = this.config.maxHeightMm;
    const connectorH = this.modules.length > 0
      ? (this.config.connector?.heightMm || 0) : 0;
    const currentH = this.dimensions().heightMm.compact;
    // Sort sizes desc by heightMm; pick the first that fits.
    const candidates = [...this.config.sizes]
      .sort((a, b) => (b.heightMm || 0) - (a.heightMm || 0));
    let chosen = candidates.find(s =>
      currentH + (s.heightMm || 0) + connectorH <= (max || Infinity)
    );
    // Prefer the configured default if it still fits.
    const defaultSize = candidates.find(s => s.id === this.config.defaultSize);
    if (defaultSize && currentH + (defaultSize.heightMm || 0) + connectorH <= (max || Infinity)) {
      chosen = defaultSize;
    }
    if (!chosen) return;

    // `at` picks the end of the stack: the "+" under the lamp adds a
    // BASE module (index 0), the "+" above it adds a TOPPER (last
    // index). The existing composition keeps its identity either way.
    const newModule = {
      sizeId:   chosen.id,
      shapeId:  this.config.defaultShape,
      finishId: this.config.defaultFinish,
      colorIdx: this.config.defaultModuleColorIdx
    };
    let newIdx;
    if (at === 'top') {
      this.modules.push(newModule);
      newIdx = this.modules.length - 1;
      if (this.modules.length > 1) {
        this.connectors.push({ colorIdx: this.config.defaultConnectorColorIdx });
      }
    } else {
      this.modules.unshift(newModule);
      newIdx = 0;
      if (this.modules.length > 1) {
        this.connectors.unshift({ colorIdx: this.config.defaultConnectorColorIdx });
      }
    }
    this.activeSlot = { kind: 'module', idx: newIdx };
    this._popTargets = new Set([newIdx]);
    this.renderStack();
    this.renderLightSection();
    // Auto-refit: the stack just grew — re-frame so the whole lamp
    // stays nicely in view.
    this.rebuildScene().then(() => this.fitCameraToStack());
    this.tickPrice();
    this.renderDimensionsPanel();
    this.nudgeIdle();
    this.syncStateToUrl();
  }

  removeModule (idx) {
    if (this.modules.length === 0) return;
    this.modules.splice(idx, 1);
    // Keep exactly modules-1 connectors (never negative).
    const wantConn = Math.max(0, this.modules.length - 1);
    if (this.connectors.length > wantConn) {
      const removeAt = Math.min(idx, this.connectors.length - 1);
      this.connectors.splice(removeAt, 1);
    }
    this.connectors.length = Math.min(this.connectors.length, wantConn);
    // No auto-open. Users found it surprising that deleting a module
    // popped a different module's card open. Clear the focus and let
    // them choose what to edit next.
    this.activeSlot = { kind: null, idx: null };
    this.renderStack();
    this.renderLightSection();
    // Auto-refit: the stack just shrank — re-frame so the remaining
    // lamp fills the view nicely instead of floating small.
    this.rebuildScene().then(() => this.fitCameraToStack());
    this.tickPrice();
    this.renderDimensionsPanel();
    this.nudgeIdle();
    this.syncStateToUrl();
  }

  // Generate a random valid lamp within the house rules — same logic
  // as the homepage preview: 2-3 modules, always an M/L body (never
  // S-only), exactly one Glow module, mono-or-duo colours, order
  // shuffled, connectors white, within the height cap. The whole stack
  // pops in.
  randomize () {
    // Same rule as addModule: rolling a build dismisses the guide
    // (covers the TOOLBAR dice — the guide's own Random button already
    // dismissed explicitly; dismissGuide is idempotent).
    this.dismissGuide();
    const cfg = this.config;
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const sizes = cfg.sizes.map(s => s.id);
    const H = Object.fromEntries(cfg.sizes.map(s => [s.id, s.heightMm || 0]));
    const connH = cfg.connector?.heightMm || 0;
    const MAX = cfg.maxHeightMm || Infinity;
    const bodies = sizes.filter(s => s !== 'S');

    // Seed with a body, then fill within the height budget.
    const stack = [pick(bodies.length ? bodies : sizes)];
    let mm = H[stack[0]];
    const target = 2 + (Math.random() < 0.6 ? 1 : 0);
    let guard = 0;
    while (stack.length < target && guard++ < 20) {
      const fits = sizes.filter(s => mm + H[s] + connH <= MAX);
      if (!fits.length) break;
      const s = pick(fits);
      stack.push(s); mm += H[s] + connH;
    }
    // shuffle order
    for (let i = stack.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [stack[i], stack[j]] = [stack[j], stack[i]];
    }

    const matte = cfg.finishes.find(f => f.id === 'matte');
    const glow  = cfg.finishes.find(f => f.id === 'glow');
    const glowIdx = Math.floor(Math.random() * stack.length);
    const mono = Math.random() < 0.55;
    const cA = pick(matte.colors).id;
    let cB = pick(matte.colors).id;
    while (!mono && cB === cA && matte.colors.length > 1) cB = pick(matte.colors).id;

    this.modules = stack.map((sizeId, i) => {
      const isGlow = i === glowIdx;
      const finish = isGlow ? glow : matte;
      const colorId = mono ? cA : (isGlow ? cA : cB);
      let colorIdx = finish.colors.findIndex(c => c.id === colorId);
      if (colorIdx < 0) colorIdx = 0;
      return { sizeId, shapeId: cfg.defaultShape, finishId: finish.id, colorIdx };
    });
    const whiteIdx = Math.max(0, cfg.connectorColors.findIndex(c => c.id === 'white'));
    this.connectors = Array.from({ length: Math.max(0, this.modules.length - 1) },
      () => ({ colorIdx: whiteIdx }));

    this.activeSlot = { kind: null, idx: null };
    this._popAll = true;
    this.renderStack();
    this.renderLightSection();
    this.rebuildScene().then(() => this.fitCameraToStack());
    this.tickPrice();
    this.renderDimensionsPanel();
    this.nudgeIdle();
    this.syncStateToUrl();
  }

  moveModule (idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= this.modules.length) return;
    const [m] = this.modules.splice(idx, 1);
    this.modules.splice(target, 0, m);
    // Move active focus with the module that the user reordered
    if (this.isActive('module', idx)) this.activeSlot = { kind: 'module', idx: target };
    this.renderStack();
    this.rebuildScene();
    this.syncStateToUrl();
  }

  setViewMode (mode) {
    if (!(mode in this.config.gaps)) return;
    this.viewMode = mode;
    this.renderModeButtons();
    // Auto-refit: Compact / Semi / Exploded change the stack height a
    // lot — re-frame so the whole lamp is always in view.
    this.rebuildScene().then(() => this.fitCameraToStack());
    // View mode changes the headline height + reshuffles the comparison
    // line in the dimensions panel.
    this.renderDimensionsPanel();
    this.syncStateToUrl();
  }

  toggleFullscreen () {
    this.fullscreen = !this.fullscreen;
    this.root.classList.toggle('is-fullscreen', this.fullscreen);
    document.body.classList.toggle('cfg-fs-lock', this.fullscreen);
    requestAnimationFrame(() => {
      this.onResize();
      this.fitCameraToStack();
    });
  }

  toggleTheme () {
    this.viewDark = !this.viewDark;
    try { localStorage.setItem('lase-view-dark', this.viewDark ? '1' : '0'); } catch (e) {}
    this.applyTheme();
  }

  // ============== Pricing & cart ==============

  // Per-module subtotal: base size price + finish modifier + shape
  // modifier. The card UI displays this both collapsed (inline in
  // the summary stripe) and expanded (footer row at the bottom of
  // the card) so the user can see what every module is actually
  // costing them.
  modulePrice (idx) {
    const m = this.modules[idx];
    if (!m) return 0;
    return this.partPrice(m.sizeId, m.shapeId, m.finishId, m.colorIdx);
  }

  // Price of one (size × shape × finish × colour) part. Prefers the
  // exact Shopify variant price from the adapter's variantMap — the
  // additive size+modifiers formula is only the bundled-config
  // fallback and knows nothing about per-variant pricing (e.g. Glow
  // costing more than Matte for the same size).
  partPrice (sizeId, shapeId, finishId, colorIdx) {
    const size   = this.config.sizes.find(s => s.id === sizeId);
    const finish = this.config.finishes.find(f => f.id === finishId);
    const shape  = this.config.shapes.find(s => s.id === shapeId);
    const color  = finish?.colors?.[colorIdx];
    const vm     = finish?.variantMap
      && finish.variantMap[(shape?.id || '') + '/' + sizeId + '/' + (color?.id || '')];
    if (vm && vm.price != null) return vm.price;
    return (size?.price || 0)
         + (finish?.priceModifier || 0)
         + (shape?.priceModifier || 0);
  }

  // "Use as" modes are really future add-on modules (light kit / vase
  // insert). No products are defined yet, so the prices default to 0 —
  // once `config.useAsPricing = { light: N, vase: N }` exists they'll
  // price in here, show a "+€N" on the toggles and land in the payload.
  useAsPrice (key) {
    return (this.config.useAsPricing && this.config.useAsPricing[key]) || 0;
  }

  totalPrice () {
    let modulesSum = 0;
    this.modules.forEach((_, i) => { modulesSum += this.modulePrice(i); });
    const lightAddon = this.config.lightAddons.find(a => a.id === this.lightAddonId);
    let useAsSum = 0;
    for (const key of ['light', 'vase']) {
      if (this.useAs?.[key]) useAsSum += this.useAsPrice(key);
    }
    return modulesSum
         + this.connectors.length * this.config.connectorPrice
         + (lightAddon?.price || 0)
         + useAsSum;
  }

  tickPrice () {
    // Query fresh — the price span lives INSIDE the Add-to-cart button
    // whose innerHTML gets swapped during the "Added ✓" flash.
    const el = this.root.querySelector('[data-cfg-price]');
    if (el) el.textContent = '€' + this.totalPrice();
  }

  // ====================================================================
  // Dimensions — height (per view mode), diameter, weight
  // ====================================================================
  //
  // Pulled from the catalogue: each size has heightMm / diameterMm /
  // weightG (auto-extract from a GLB isn't reliable for diameter
  // (printer skirts / surface ribs) and impossible for weight (depends
  // on infill + walls + density), so we treat them as hand-set
  // metadata per part). Connector + light add-on contribute too.
  //
  // The same numbers feed:
  //   • the right-column summary block (renderDimensionsPanel)
  //   • the 3D koty overlays (updateDimensionsOverlay, called from
  //     animate() so projection follows the orbit)

  dimensions () {
    const cfg = this.config;
    const sizesById = Object.fromEntries(cfg.sizes.map(s => [s.id, s]));
    const connectorH = cfg.connector?.heightMm || 0;
    const moduleCount = this.modules.length;

    let modulesHeight = 0;
    let modulesWeight = 0;
    let maxDiameter   = 0;
    this.modules.forEach(m => {
      const size = sizesById[m.sizeId];
      if (!size) return;
      modulesHeight += size.heightMm || 0;
      modulesWeight += size.weightG  || 0;
      if (size.diameterMm > maxDiameter) maxDiameter = size.diameterMm;
    });
    if (cfg.connector?.diameterMm && cfg.connector.diameterMm > maxDiameter) {
      maxDiameter = cfg.connector.diameterMm;
    }

    const connectorTotal = (moduleCount - 1) * connectorH;
    // View-mode gaps in mm (configurator-config.js stores them in metres).
    const gapMm = {
      compact:  (cfg.gaps?.compact  || 0) * 1000,
      semi:     (cfg.gaps?.semi     || 0) * 1000,
      exploded: (cfg.gaps?.exploded || 0) * 1000
    };
    const baseHeight = modulesHeight + connectorTotal;
    const heightMm = {
      compact:  Math.round(baseHeight + (moduleCount - 1) * gapMm.compact),
      semi:     Math.round(baseHeight + (moduleCount - 1) * gapMm.semi),
      exploded: Math.round(baseHeight + (moduleCount - 1) * gapMm.exploded)
    };

    const lightAddon  = cfg.lightAddons.find(a => a.id === this.lightAddonId);
    const lightWeight = lightAddon?.weightG || 0;
    const connectorWeight = (moduleCount - 1) * (cfg.connector?.weightG || 0);
    const weightG = modulesWeight + connectorWeight + lightWeight;

    return { heightMm, diameterMm: maxDiameter, weightG };
  }

  // ---- Height budget --------------------------------------------------
  //
  // The configurator caps total compact-mode height at
  // `config.maxHeightMm` instead of capping module COUNT. A stack of
  // 6 Small modules is roughly the same size as 2 Larges, but the
  // count-based cap let users build flag-poles. Real-world build
  // sense (and shipping reality) is height-driven.
  //
  // `canAddModule()` returns true when the smallest possible module
  // (S + a connector) still fits under the cap; `wouldFitSize(idx,
  // sizeId)` checks whether resizing an existing module to `sizeId`
  // would push us over.

  smallestSizeId () {
    // Pick the size with the lowest heightMm — order-of-declaration
    // independent so swapping S/M/L positions doesn't break the cap.
    return this.config.sizes.reduce((min, s) =>
      (!min || s.heightMm < min.heightMm) ? s : min, null
    )?.id;
  }

  canAddModule () {
    const max = this.config.maxHeightMm;
    if (!max) return this.modules.length < (this.config.maxModules || 12);
    if (this.modules.length >= (this.config.maxModules || 12)) return false;

    const smallest = this.config.sizes.find(s => s.id === this.smallestSizeId());
    const newModuleHeight    = smallest?.heightMm || 0;
    const newConnectorHeight = this.modules.length > 0
      ? (this.config.connector?.heightMm || 0)
      : 0;
    return this.dimensions().heightMm.compact + newModuleHeight + newConnectorHeight <= max;
  }

  wouldFitSize (idx, sizeId) {
    const max = this.config.maxHeightMm;
    if (!max) return true;
    const mod = this.modules[idx];
    if (!mod) return true;
    const oldSize = this.config.sizes.find(s => s.id === mod.sizeId);
    const newSize = this.config.sizes.find(s => s.id === sizeId);
    if (!newSize) return false;
    const delta = (newSize.heightMm || 0) - (oldSize?.heightMm || 0);
    return this.dimensions().heightMm.compact + delta <= max;
  }

  renderDimensionsPanel () {
    if (!this.dimsRoot) return;
    const d = this.dimensions();
    // The summary panel always shows two heights side-by-side:
    // compact + semi. We skip exploded because that's a UX-only
    // separation — the vase doesn't physically hold together at
    // 60 mm gaps, so it'd be misleading to quote it as a dimension.
    const set = (sel, val) => {
      const el = this.dimsRoot.querySelector(sel);
      if (el) el.textContent = val;
    };
    // Empty stack → em-dashes, not "0 mm / −15 mm" nonsense.
    const empty = this.modules.length === 0;
    set('[data-dim-height-compact]', empty ? '—' : d.heightMm.compact);
    set('[data-dim-height-semi]',    empty ? '—' : d.heightMm.semi);
    set('[data-dim-diameter]',       empty ? '—' : d.diameterMm);
    set('[data-dim-weight]',         empty ? '—' : d.weightG);
  }

  // 3D overlay koty — projection-driven HTML labels that hug the
  // model's silhouette. Two pieces:
  //   • height label  — vertical line + value pinned to the LEFT
  //                      edge of the stack, vertically centred.
  //   • diameter label — short horizontal line + value pinned BELOW
  //                      the widest module.
  toggleDimsOverlay () {
    this.showDimsOverlay = !this.showDimsOverlay;
    const btn = this.root.querySelector('[data-cfg-toggle-dims]');
    if (btn) btn.classList.toggle('is-active', this.showDimsOverlay);
    this.updateDimensionsOverlay();
  }

  updateDimensionsOverlay () {
    if (!this.dimsOverlay) return;
    // Toggled off by default; user reveals via the ruler icon.
    if (!this.showDimsOverlay) {
      this.dimsOverlay.setAttribute('hidden', '');
      return;
    }
    if (!this.partsGroup || !this.partsGroup.children.length) {
      this.dimsOverlay.setAttribute('hidden', '');
      return;
    }
    this.dimsOverlay.removeAttribute('hidden');

    const box = this.computeMeshBox(this.partsGroup, this._tmpDimBox ||= new THREE.Box3());
    const v   = (this._tmpDimVec ||= new THREE.Vector3());

    const projectPx = (worldX, worldY, worldZ) => {
      v.set(worldX, worldY, worldZ).project(this.camera);
      const rect = this.canvas.getBoundingClientRect();
      const sx = ( v.x * 0.5 + 0.5) * rect.width;
      const sy = (-v.y * 0.5 + 0.5) * rect.height;
      return { x: sx, y: sy, behind: v.z > 1 };
    };

    const d = this.dimensions();
    const heightCurrent = d.heightMm[this.viewMode] || d.heightMm.compact;

    // ----- Camera-relative anchors for both kotas
    //
    // Both the height line and the diameter line need anchor points
    // that don't rotate with the vase. We compute the camera's
    // horizontal viewing direction (camForward) and its perpendicular
    // in the XZ plane (camRight) once, then use them to build:
    //   - HEIGHT kota: front-LEFT corner of an inscribed bounding
    //     square at  centre − (camRight + camForward) × radius.
    //     Always renders to the left of the visible silhouette as
    //     the user orbits, mirroring how it used to sit at
    //     (box.min.x, *, box.max.z) — but rotation-stable.
    //   - DIAMETER kota: chord centred at  centre − camForward × radius
    //     with endpoints at ±radius along camRight. Always horizontal
    //     in screen space, tangent to the camera-facing bottom edge
    //     of the silhouette.
    const center = (this._tmpDimCenter ||= new THREE.Vector3());
    center.set(
      (box.min.x + box.max.x) / 2,
       box.min.y,
      (box.min.z + box.max.z) / 2
    );
    const radius = Math.max(
      (box.max.x - box.min.x) / 2,
      (box.max.z - box.min.z) / 2
    );

    const camForward = (this._tmpDimFwd ||= new THREE.Vector3());
    this.camera.getWorldDirection(camForward);
    camForward.y = 0;
    if (camForward.lengthSq() < 1e-6) {
      // Camera straight up/down — fall back to world Z so we still
      // emit a line rather than a zero-length artefact.
      camForward.set(0, 0, 1);
    } else {
      camForward.normalize();
    }
    const camRight = (this._tmpDimRight ||= new THREE.Vector3());
    camRight.set(camForward.z, 0, -camForward.x); // = worldUp × camForward
    // (Cross product simplifies to this in the XZ plane.)

    // ----- Height kota (camera-left side of the silhouette)
    //
    // Note: despite the name, `camRight` (= worldUp × camForward)
    // actually points to screen-LEFT in Three.js's right-handed world
    // coordinates — verified for the default camera-on-+Z case where
    // camForward = (0,0,-1) yields camRight = (-1,0,0) = world −X =
    // screen left. So we ADD camRight*offset to push the line out to
    // the camera-left side of the vase.
    //
    // Offset multiplier 1.5× radius (was 1.0 at the inscribed-square
    // corner): gives the vertical line breathing room rather than
    // hugging the silhouette. The camForward term keeps it in front
    // of the body so it doesn't poke through the back.
    const H_LEFT_OFFSET = 1.5;
    const hX = center.x + (camRight.x * H_LEFT_OFFSET - camForward.x) * radius;
    const hZ = center.z + (camRight.z * H_LEFT_OFFSET - camForward.z) * radius;
    const hTop = projectPx(hX, box.max.y,                       hZ);
    const hBot = projectPx(hX, box.min.y,                       hZ);
    const hMid = projectPx(hX, (box.min.y + box.max.y) / 2,     hZ);

    const lineEl  = this.dimsOverlay.querySelector('[data-dim-height-line]');
    const labelEl = this.dimsOverlay.querySelector('[data-dim-height-label]');

    if (hTop.behind || hBot.behind) {
      if (lineEl)  lineEl.style.display  = 'none';
      if (labelEl) labelEl.style.display = 'none';
    } else if (lineEl && labelEl) {
      lineEl.style.display = '';
      labelEl.style.display = '';
      const top    = Math.min(hTop.y, hBot.y);
      const height = Math.abs(hBot.y - hTop.y);
      // Average the two endpoints' projected X — by symmetry they're
      // identical, but averaging smooths floating-point drift on
      // extreme camera angles (same trick we use for the diameter).
      const avgX = (hTop.x + hBot.x) / 2;
      lineEl.style.left   = `${avgX}px`;
      lineEl.style.top    = `${top}px`;
      lineEl.style.height = `${height}px`;
      labelEl.style.left  = `${hMid.x}px`;
      labelEl.style.top   = `${hMid.y}px`;
      labelEl.textContent = `${heightCurrent} mm`;
    }

    // ----- Diameter kota (camera-facing tangent chord, horizontal in
    //       screen space)
    const chordX = center.x - camForward.x * radius;
    const chordZ = center.z - camForward.z * radius;

    const dxLeft  = projectPx(chordX - camRight.x * radius, center.y, chordZ - camRight.z * radius);
    const dxRight = projectPx(chordX + camRight.x * radius, center.y, chordZ + camRight.z * radius);
    const dxMid   = projectPx(chordX,                        center.y, chordZ);

    // Diameter kota RETIRED — it's always 95 mm, and its label under
    // the base collided with the add-module FAB. Height is the only
    // dimension worth annotating in 3D (Ø stays in the bottom bar).
    const dLineEl  = this.dimsOverlay.querySelector('[data-dim-diameter-line]');
    const dLabelEl = this.dimsOverlay.querySelector('[data-dim-diameter-label]');
    if (dLineEl)  dLineEl.style.display  = 'none';
    if (dLabelEl) dLabelEl.style.display = 'none';
    void dxLeft; void dxRight; void dxMid;
  }

  // ====================================================================
  // URL state — shareable / saveable configuration
  // ====================================================================
  //
  // Format (kept compact + human-readable so it survives copy/paste):
  //
  //   #c=v1!<modules>!<connectors>!<light>!<viewMode>
  //
  //   modules    = comma-list of  <size>-<shape>-<finish>-<color>
  //                e.g.  M-lantern-matte-moss,L-lantern-glow-marigold
  //   connectors = comma-list of  <color>
  //                e.g.  white,white
  //   light      = light-addon id (e.g. none / led-puck / bulb)
  //   viewMode   = compact / semi / exploded
  //
  // The runtime calls syncStateToUrl() after every state mutation
  // (debounced via requestAnimationFrame), and loadStateFromUrl() at
  // construction time + when `from=PRODUCT_HANDLE` is passed in the
  // querystring (planned PDP "Customize" preload flow).
  //
  // ====================================================================

  encodeState () {
    const cfg = this.config;

    const moduleTokens = this.modules.map(m => {
      const finish = cfg.finishes.find(f => f.id === m.finishId);
      const color  = finish?.colors[m.colorIdx];
      const colorId = (color?.id)
                   || (color?.name || '').toLowerCase().replace(/\s+/g, '-')
                   || '0';
      return [m.sizeId, m.shapeId, m.finishId, colorId].join('-');
    }).join(',');

    const connectorTokens = this.connectors.map(c => {
      const col = cfg.connectorColors[c.colorIdx];
      return (col?.id) || (col?.name || '').toLowerCase().replace(/\s+/g, '-') || '0';
    }).join(',');

    return [
      'v1',
      moduleTokens,
      connectorTokens,
      this.lightAddonId || 'none',
      this.viewMode || 'compact'
    ].join('!');
  }

  decodeState (raw) {
    if (!raw) return null;
    // Accept the hash with or without the leading marker chars
    let str = String(raw).replace(/^#/, '').replace(/^c=/, '');
    if (!str.startsWith('v1!')) return null;

    const sections = str.slice(3).split('!');
    if (sections.length < 4) return null;
    const [modStr, connStr, lightId, viewMode] = sections;
    const cfg = this.config;

    const modules = (modStr || '').split(',').filter(Boolean).map(token => {
      const parts = token.split('-');
      // colour ids may themselves contain dashes ("smoke-grey") so
      // re-join anything past index 3.
      let [sizeId, shapeId, finishId, ...colorParts] = parts;
      const colorId = colorParts.join('-');
      const finish = cfg.finishes.find(f => f.id === finishId);
      if (!finish) return null;
      // Unknown shape id (stale preset hash from before a pattern
      // rename) → snap to the catalogue default so variantMap lookups
      // and pattern-aware model keys still resolve.
      if (!cfg.shapes.some(s => s.id === shapeId)) {
        shapeId = cfg.defaultShape || (cfg.shapes[0] && cfg.shapes[0].id) || shapeId;
      }
      let colorIdx = finish.colors.findIndex(c => c.id === colorId);
      if (colorIdx < 0) colorIdx = 0;
      return { sizeId, shapeId, finishId, colorIdx };
    }).filter(Boolean);

    const connectors = (connStr || '').split(',').filter(Boolean).map(cid => {
      let colorIdx = cfg.connectorColors.findIndex(c => c.id === cid);
      if (colorIdx < 0) colorIdx = 0;
      return { colorIdx };
    });

    return {
      modules,
      connectors,
      lightAddonId: lightId || 'none',
      viewMode:     viewMode || 'compact'
    };
  }

  applyState (state) {
    if (!state) return false;
    const cfg = this.config;
    let touched = false;

    if (Array.isArray(state.modules) && state.modules.length) {
      const clamp = state.modules.slice(0, cfg.maxModules);
      // Drop any module whose size/finish references are unknown.
      const valid = clamp.filter(m => {
        const finish = cfg.finishes.find(f => f.id === m.finishId);
        const size   = cfg.sizes.find(s => s.id === m.sizeId);
        return finish && size;
      });
      // Reject S-only presets too — a stale/hand-edited hash must not
      // load a build the picker itself would never allow.
      if (valid.length >= cfg.minModules && valid.some(m => m.sizeId !== 'S')) {
        this.modules = valid;
        touched = true;
      }
    }
    if (Array.isArray(state.connectors)) {
      this.connectors = state.connectors.slice(0, Math.max(0, this.modules.length - 1));
      // Pad with default if the URL was short
      while (this.connectors.length < this.modules.length - 1) {
        this.connectors.push({ colorIdx: cfg.defaultConnectorColorIdx });
      }
      touched = true;
    }
    if (state.lightAddonId && cfg.lightAddons.some(a => a.id === state.lightAddonId)) {
      this.lightAddonId = state.lightAddonId;
      touched = true;
    }
    if (state.viewMode && (state.viewMode in cfg.gaps)) {
      this.viewMode = state.viewMode;
      touched = true;
    }
    return touched;
  }

  // Push current state to location.hash. Debounced via rAF so a burst
  // of mutations (e.g. drag-reorder) only writes the URL once.
  syncStateToUrl () {
    // While the start guide shows, keep the URL pristine — writing the
    // empty state would make a plain reload look like a deep-link and
    // skip the guide.
    if (this.showGuide) return;
    if (this._urlSyncPending) return;
    this._urlSyncPending = true;
    requestAnimationFrame(() => {
      this._urlSyncPending = false;
      try {
        const hash = '#c=' + this.encodeState();
        if (location.hash !== hash) {
          history.replaceState(null, '', hash);
        }
      } catch (e) { /* noop — hash updates are best-effort */ }
    });
  }

  loadStateFromUrl () {
    const hash = location.hash || '';
    if (!hash.startsWith('#c=')) return false;
    const state = this.decodeState(hash);
    return this.applyState(state);
  }

  // ----- Order payload builder ------------------------------------
  //
  // Returns a structured object describing the user's full
  // configuration in a form the 3D-print backend / invoicing flow
  // can consume directly. Every part carries:
  //   • a unique runtime line id (so the UI can reference it)
  //   • the customer-facing label
  //   • the SHOP SKU (used on the invoice / order line)
  //   • the production-side info (material, filament, supplier SKU)
  //   • the price contribution in EUR
  //
  // The same payload is what gets:
  //   • shown to the user in the Add-to-Cart confirmation modal
  //   • dispatched on the `configurator:add-to-cart` event so
  //     external code can POST to /cart/add.js, Shopify Functions,
  //     a Make/Zapier webhook, or whatever fulfilment system the
  //     store is wired up to.
  buildOrderPayload () {
    const cfg = this.config;
    const sizesById = Object.fromEntries(cfg.sizes.map(s => [s.id, s]));
    const lines = [];
    let total = 0;

    for (let i = 0; i < this.modules.length; i++) {
      const mod    = this.modules[i];
      const size   = sizesById[mod.sizeId];
      const finish = cfg.finishes.find(f => f.id === mod.finishId);
      const baseColor  = finish.colors[mod.colorIdx];
      const shape  = cfg.shapes.find(s => s.id === mod.shapeId) || cfg.shapes[0];

      // Cart-line resolution: visual hex is shared across all
      // patterns + sizes (lean admin: edit a colour once in the
      // product palette). But the cart line MUST reference the exact
      // Shopify variant for the (finish × pattern × size × color)
      // combination — otherwise checkout bills wrong and the ERP
      // print backend schedules the wrong STL.
      //
      // `finish.variantMap` is built by the adapter from the new
      // (Pattern × Size × Color) variants; we still tolerate the
      // older bundled-config shape (`color.bySize[sizeId]`) so a
      // dev preview against the bundled catalogue keeps working.
      const variantKey = (shape?.id || '') + '/' + mod.sizeId + '/' + (baseColor?.id || '');
      const fromMap    = finish.variantMap && finish.variantMap[variantKey];
      const legacy     = baseColor && baseColor.bySize && baseColor.bySize[mod.sizeId];
      const color      = fromMap || legacy
                           ? { ...baseColor, ...(fromMap || legacy) }
                           : baseColor;

      const sku   = (size.sku || '')
                  + (shape.skuSuffix || '')
                  + (finish.skuSuffix || '')
                  + '-' + (color.id || color.name.toLowerCase());
      const price = color.price
                    ?? ((size.price || 0)
                        + (finish.priceModifier || 0)
                        + (shape.priceModifier  || 0));
      total += price;

      lines.push({
        line: lines.length + 1,
        kind: 'module',
        position: i,                       // 0 = bottom, length-1 = top
        label:  `${size.name} module · ${shape.name} · ${finish.name} · ${color.name}`,
        sku,                               // Sestava SKU (e.g. M-P01-MAT-BLU)
        variantId:   color.variantId || null,
        // ERP part decomposition: the sestava breaks into these two
        // physical parts at print time. Carrying them on the order
        // line property means the print backend can schedule jobs
        // without a separate Shopify-to-ERP lookup.
        erpPartSkus: {
          ext: color.extPartSku || null,
          int: color.intPartSku || null
        },
        size:        { id: size.id, name: size.name, heightMm: size.heightMm },
        shape:       { id: shape.id, name: shape.name },
        finish:      { id: finish.id, name: finish.name, material: finish.material },
        color:       { id: color.id || null, name: color.name,
                       ext: color.ext, int: color.int,
                       filamentExt: color.filamentExt || color.filament || null,
                       filamentInt: color.filamentInt || color.filament || null,
                       // Legacy field kept for any downstream code
                       // still reading `filament` / `filamentSku`.
                       filament: color.filamentExt || color.filament || null,
                       filamentSku: color.filamentSku || null },
        material:    size.material,        // base print material category
        price
      });
    }

    // Connectors — one fewer than modules (they sit between).
    for (let i = 0; i < this.connectors.length; i++) {
      const conn = this.connectors[i];
      const c    = cfg.connectorColors[conn.colorIdx];
      lines.push({
        line:  lines.length + 1,
        kind:  'connector',
        between: [i, i + 1],
        label: `Connector · ${c.name}`,
        sku:   c.sku || ((cfg.connector?.sku || 'MONO-CONN')
                + '-' + (c.id || c.name.toLowerCase())),
        // Connector colour records come straight from the Shopify
        // Konektor product variants, so each carries its own
        // Shopify ProductVariant id. Surfacing it here is what
        // lets the cart adapter POST the right SKU to /cart/add.js.
        variantId: c.variantId || null,
        heightMm: cfg.connector?.heightMm || null,
        material: cfg.connector?.material || null,
        color:    { id: c.id || null, name: c.name, ext: c.ext,
                    filament: c.filament || null, filamentSku: c.filamentSku || null },
        price:    cfg.connectorPrice
      });
      total += cfg.connectorPrice;
    }

    const lightAddon = cfg.lightAddons.find(a => a.id === this.lightAddonId);
    if (lightAddon && lightAddon.id !== 'none') {
      lines.push({
        line:  lines.length + 1,
        kind:  'light_addon',
        label: lightAddon.name,
        sku:   lightAddon.sku || '',
        material: lightAddon.material || null,
        price: lightAddon.price
      });
      total += lightAddon.price;
    }

    // "Use as" add-on modules — priced lines once useAsPricing is
    // configured (no products defined yet, so usually price 0 → flag
    // only).
    for (const key of ['light', 'vase']) {
      const p = this.useAsPrice(key);
      const prod = this.config.useAsProducts?.[key];
      if (this.useAs?.[key] && p > 0) {
        lines.push({
          line:  lines.length + 1,
          kind:  'use_as',
          id:    key,
          label: key === 'light' ? 'Light mode' : 'Glass vase insert',
          // Real product line — the glass variant lands in the cart
          // (and the ERP matches its SKU like any other part).
          variantId: prod?.variantId || null,
          sku:       prod?.sku || null,
          price: p
        });
        total += p;
      }
    }

    return {
      currency: 'EUR',
      total,
      moduleCount:    this.modules.length,
      connectorCount: this.connectors.length,
      viewMode:       this.viewMode,
      // "Use as" flags from the bottom bar — both can be on at once.
      useAs:          { ...(this.useAs || { light: true, vase: true }) },
      lines
    };
  }

  // Wraps buildOrderPayload + the Shopify /cart/add.js POST so
  // clicking the Add-to-Cart button actually deposits the
  // configured stack as cart line items (with the ERP properties
  // each module needs). On success the theme's cart drawer opens
  // (slides in from the side) so the customer can review or
  // continue shopping — no full-page redirect.
  //
  // External listeners can still observe the `configurator:add-to-cart`
  // CustomEvent if they want to mirror the order to analytics or a
  // backend webhook.
  async summariseConfiguration () {
    // Guard: never add an invalid build (button is disabled too, but
    // this covers keyboard / programmatic triggers).
    const v = this.buildValidity();
    if (!v.ok) { this.refreshCartValidity(); return; }
    const payload = this.buildOrderPayload();

    // Custom event so any external script can pick the order up
    // and mirror it to analytics, ERP, etc.
    this.root.dispatchEvent(new CustomEvent('configurator:add-to-cart', {
      bubbles: true,
      detail:  payload
    }));
    console.log('[configurator] Add-to-cart payload:', payload);

    const cartBtn = this.root.querySelector('[data-cfg-add]');
    if (cartBtn) cartBtn.disabled = true;

    try {
      await addPayloadToShopifyCart(payload);
      // Successful — show a brief "Added" affordance on the CTA so
      // the click reads as confirmed even before the drawer slides
      // in. Re-enabled after a short pause so the user can add a
      // second configuration without reloading the page.
      if (cartBtn) {
        const original = cartBtn.innerHTML;
        cartBtn.textContent = 'Added to cart ✓';
        setTimeout(() => {
          cartBtn.innerHTML = original;
          cartBtn.disabled = false;
          this.tickPrice();
        }, 1800);
      }
    } catch (err) {
      console.error('[configurator] cart add failed', err);
      // Surface a minimal, non-modal error so the user knows the
      // click didn't silently fail. Re-enable the button so they
      // can retry once they've e.g. reconnected to wifi.
      if (cartBtn) cartBtn.disabled = false;
      // Inline error message under the CTA — falls back to alert
      // when there's no element to host the message.
      const footer = this.root.querySelector('.cfg__bar-total');
      if (footer) {
        let err_el = footer.querySelector('[data-cfg-error]');
        if (!err_el) {
          err_el = document.createElement('p');
          err_el.dataset.cfgError = '1';
          err_el.className = 'cfg__error';
          footer.appendChild(err_el);
        }
        err_el.textContent = 'Couldn’t add to cart. ' + (err.message || 'Please try again.');
      } else {
        alert('Couldn’t add to cart: ' + (err.message || err));
      }
    }
  }
}

// ---- Shopify cart adapter -----------------------------------------
//
// Posts the configurator order payload to /cart/add.js. Each
// non-variant-backed line (e.g. unmapped light add-on) is skipped
// with a console warning; the rest land as cart line items with the
// ERP-relevant line item properties needed downstream by the
// /hooks/shopify/order-paid webhook (see docs/erp-webhook.md).
//
// `_*`-prefixed properties are hidden from the customer-facing
// receipt but still visible to merchants in admin + order webhooks.
// `_stack_id` groups all lines from the same Add-to-Cart click so
// the ERP can reassemble the printed object.
async function addPayloadToShopifyCart (payload) {
  const stackId = 'stack-' + Date.now().toString(36) + '-' +
                  Math.random().toString(36).slice(2, 8);

  const items = [];
  for (const line of (payload.lines || [])) {
    if (!line.variantId) {
      console.warn('[configurator] line without variantId — skipped:', line);
      continue;
    }
    const properties = {
      _kind:     line.kind || '',
      _position: String(line.position ?? line.line ?? 0),
      _stack_id: stackId
    };
    if (line.erpPartSkus?.ext)  properties._ext_part_sku = line.erpPartSkus.ext;
    if (line.erpPartSkus?.int)  properties._int_part_sku = line.erpPartSkus.int;
    if (line.color?.filamentExt) properties._filament_ext = line.color.filamentExt;
    if (line.color?.filamentInt) properties._filament_int = line.color.filamentInt;
    if (line.color?.filament && !properties._filament_ext) {
      properties._filament_ext = line.color.filament;
    }

    items.push({ id: line.variantId, quantity: 1, properties });
  }

  if (items.length === 0) {
    throw new Error('Nothing to add — none of the configured lines map to a Shopify variant. Edit your sestava products in admin and try again.');
  }

  const addRes = await fetch('/cart/add.js', {
    method:  'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body:    JSON.stringify({ items })
  });

  if (!addRes.ok) {
    let detail = '';
    try { detail = (await addRes.json()).description || ''; } catch (_e) {}
    throw new Error('HTTP ' + addRes.status + (detail ? ' — ' + detail : ''));
  }

  // Fetch the full cart state so we can hand a fresh `item_count`
  // to the theme's cart bubble + cart-items refresh. /cart/add.js
  // only returns the line items just added, not the whole cart.
  let cart = null;
  try {
    cart = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } }).then(r => r.json());
  } catch (_e) { /* drawer falls back to its own fetch if needed */ }

  // Dispatch the theme's `cart:update` event (eventName from
  // assets/events.js → ThemeEvents.cartUpdate). The cart bubble
  // re-renders the badge count, the cart-items component refreshes
  // its rendered section, and the cart-drawer-component auto-opens
  // if the `auto_open_cart_drawer` theme setting is on.
  document.dispatchEvent(new CustomEvent('cart:update', {
    bubbles: true,
    detail: {
      resource: cart || undefined,
      sourceId: 'configurator',
      data: {
        source:    'configurator',
        itemCount: cart?.item_count,
        // Empty sections map — the cart-items component falls back
        // to `sectionRenderer.renderSection` when no inline HTML is
        // provided, which is exactly what we want.
        sections:  {}
      }
    }
  }));

  // Force-open the cart drawer regardless of the auto-open theme
  // setting — adding a 5-line configured stack is a deliberate
  // action that deserves a visible confirmation. Quietly no-ops if
  // the theme doesn't render a drawer (e.g. password page).
  const drawer = document.querySelector('cart-drawer-component');
  if (drawer && typeof drawer.open === 'function') drawer.open();

  // Belt-and-braces: paint the drawer surface with inline !important
  // after open. Diagnostic from the live preview showed even
  // `background-color: #ffffff !important` in author CSS was being
  // overridden somewhere (computed value: rgba(0,0,0,0)). Inline
  // styles with !important via setProperty are the strongest author
  // declaration — only inline-style-attribute !important and UA
  // !important can beat them, and neither exists for this property.
  // Runs after the drawer DOM has been updated for the new cart
  // state.
  requestAnimationFrame(() => {
    const dlg = drawer && drawer.querySelector('.cart-drawer__dialog');
    if (!dlg) return;
    const isDark = document.documentElement.classList.contains('is-dark');
    const bg = isDark ? '#0d0d0c' : '#ffffff';
    const fg = isDark ? '#e8e5dc' : '#1a1916';
    const surfaces = [dlg, ...dlg.querySelectorAll(
      '.cart-drawer__inner, .cart-drawer__content, .cart-drawer__items, .cart-drawer__summary, .cart-drawer__header, cart-items-component'
    )];
    for (const el of surfaces) {
      el.style.setProperty('background-color', bg, 'important');
      el.style.setProperty('background-image', 'none',  'important');
      el.style.setProperty('color',            fg, 'important');
    }
  });

  return addRes.json();
}

// ============== Bootstrap ==============

// ----- Shopify-parts → runtime config adapter -----------------------
//
// When the section emits a [data-cfg-parts] script (because the
// store has products in the configured parts collection), translate
// the raw Shopify product JSON into the same shape the runtime uses
// internally — sizes / finishes / colors / connectorColors /
// lightAddons. Returns a partial config that gets merged OVER the
// bundled DEFAULT_CONFIG so missing categories fall back gracefully.
function configFromShopifyParts (parts) {
  if (!Array.isArray(parts) || parts.length === 0) return null;

  const SIZE_NAMES = { S: 'Small', M: 'Medium', L: 'Large' };
  const SIZE_ORDER = { S: 1, M: 2, L: 3 };

  // ---- Modules: ONE PRODUCT PER FINISH (Matte / Glow) -------------
  // Each product carries:
  //   • product-level `palette` (JSON metafield) — shared colour list
  //   • product-level `patterns` (resolved metaobjects) — 3D models +
  //     dimensions per size for every shape pattern
  //   • variants emitted as (Pattern × Size × Color) combinations,
  //     each holding only ERP-relevant metafields (color_id,
  //     ext_part_sku, int_part_sku, filament_ext, filament_int)
  //
  // The adapter fans these into the runtime's shapes / sizes /
  // finishes records and builds a `variantMap` keyed by
  // "<pattern>/<size>/<color_id>" so the cart-line builder can look
  // up the exact Shopify variantId for any (finish × pattern × size
  // × color) combination.
  const moduleProducts = parts.filter(p => p.metafields?.role === 'module');
  const sizeMap        = new Map();   // sizeId    → size record
  const shapeMap       = new Map();   // patternId → shape record
  const finishMap      = new Map();   // finishId  → finish record
  const modelOverrides = {};

  // Some Shopify Liquid contexts emit json-type metafields as a raw
  // JSON string instead of pre-parsed values — be tolerant of both.
  const asArray = (val) => {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string' && val.trim().startsWith('[')) {
      try { return JSON.parse(val); } catch (_e) { return []; }
    }
    return [];
  };

  for (const prod of moduleProducts) {
    const mf       = prod.metafields || {};
    const finishId = mf.finish_id;
    if (!finishId) continue;

    const palette  = asArray(mf.palette);
    const patterns = asArray(mf.patterns);

    // Skip legacy v1 sestava products (one product per size × finish,
    // with per-variant hex_ext / hex_int). The v2 schema requires the
    // product to carry both a palette and at least one pattern_ref.
    // Without this guard a leftover v1 product would claim the
    // finishMap slot before its v2 sibling, leaving the configurator
    // with empty colours + an empty variantMap.
    if (palette.length === 0 && patterns.length === 0) continue;

    // Warn (once per product) if palette / patterns came in as
    // something other than an array — usually a sign the Liquid layer
    // serialised a JSON-type metafield as a raw string. `asArray()`
    // already parses around it, but the warning surfaces the cause
    // immediately instead of letting the configurator silently fall
    // back to the bundled palette.
    if (mf.palette && !Array.isArray(mf.palette)) {
      console.warn('[configurator] palette for "%s" arrived as %s — parsed via asArray()', prod.handle, typeof mf.palette);
    }
    if (mf.patterns && !Array.isArray(mf.patterns)) {
      console.warn('[configurator] patterns for "%s" arrived as %s — parsed via asArray()', prod.handle, typeof mf.patterns);
    }

    for (const pat of patterns) {
      const patternId = pat.pattern_id;
      if (!patternId) continue;

      if (!shapeMap.has(patternId)) {
        shapeMap.set(patternId, {
          id:            patternId,
          name:          pat.name || patternId,
          skuSuffix:     '-' + patternId,
          ready:         true,
          priceModifier: 0,
          hint:          '',
          sort:          Number(pat.sort) || 0
        });
      }

      // Register every size the pattern carries. Heights / weights
      // are per-size on the pattern metaobject; diameter is one
      // value per pattern.
      for (const sizeId of ['S', 'M', 'L']) {
        if (!sizeMap.has(sizeId)) {
          sizeMap.set(sizeId, {
            id:         sizeId,
            name:       SIZE_NAMES[sizeId] || sizeId,
            heightMm:   Number(pat.heights_mm?.[sizeId]) || 0,
            diameterMm: Number(pat.diameter_mm)          || 0,
            weightG:    Number(pat.weights_g?.[sizeId])  || 0,
            sku:        sizeId,
            material:   mf.material || '',
            price:      0
          });
        }

        const modelUrl = pat.models?.[sizeId];
        if (modelUrl) {
          // Pattern-aware key consumed by `rebuildScene`. The renderer
          // composes `module_<pattern>_<size>` and falls back to the
          // size-only key for backward compat.
          modelOverrides['module_' + patternId + '_' + sizeId] = modelUrl;
          if (!modelOverrides['module_' + sizeId]) {
            modelOverrides['module_' + sizeId] = modelUrl;
          }
        }
      }
    }

    // --- Finish record (palette + variant lookup) ---
    if (!finishMap.has(finishId)) {
      finishMap.set(finishId, {
        id:        finishId,
        name:      finishId === 'matte' ? 'Matte' : finishId === 'glow' ? 'Glow' : finishId,
        // Customer-facing product title (e.g. "01 MATTE") — used as the
        // card title in the "your build" summary.
        productTitle: prod.title || '',
        skuSuffix: finishId === 'matte' ? '-MAT' : finishId === 'glow' ? '-GLW' : '-' + finishId.toUpperCase(),
        material:  mf.material || '',
        priceModifier: 0,
        hint:      '',
        // Visual palette — shared across patterns + sizes of this
        // finish. EXT and INT hex live here, no per-variant overrides
        // (the lean architecture goal: change a colour in one place
        // and every sestava using it updates).
        colors: palette
          .slice()
          .sort((a, b) => (Number(a.sort) || 0) - (Number(b.sort) || 0))
          .map(c => ({
            id:     c.id,
            name:   c.name,
            ext:    c.hex_ext || '#888',
            int:    c.hex_int || c.hex_ext || '#888',
            // `swatch` is the colour drawn in the COLOR PICKER UI
            // (the round dots in the side panel). Decouples the
            // "marketing colour" the merchant wants to communicate
            // from the literal EXT plastic hex used in 3D. Optional
            // — falls back to hex_ext if not provided, so existing
            // palettes keep working unchanged.
            swatch: c.hex_swatch || c.hex_ext || '#888',
            sort:   Number(c.sort) || 0
          })),
        // Variant lookup: "<patternId>/<sizeId>/<colorId>" → record
        // used by buildOrderPayload to emit the correct cart line.
        variantMap: {}
      });
    }
    const finishRec = finishMap.get(finishId);

    // --- Variants ---
    for (const v of (prod.variants || [])) {
      const opts      = v.options || {};
      // Pattern resolution: prefer the variant's Pattern option (legacy
      // schema where Pattern × Size × Color were all variant options).
      // When the product no longer carries a Pattern option — the new
      // "one product per finish × pattern" schema, where pattern is
      // baked into the product (01 MATTE / 01 GLOW) and only Size ×
      // Color remain as variant options — fall back to the product's
      // single pattern from its pattern_refs metaobject. Backward
      // compatible: products that still have the Pattern option are
      // unaffected.
      const patternId = opts.Pattern || opts.pattern || (patterns[0] && patterns[0].pattern_id) || '';
      const sizeId    = opts.Size    || opts.size    || '';
      const cmf       = v.metafields || {};
      const colorId   = cmf.color_id || (opts.Color || '').toLowerCase();
      if (!patternId || !sizeId || !colorId) continue;

      finishRec.variantMap[patternId + '/' + sizeId + '/' + colorId] = {
        variantId:   v.id,
        sku:         v.sku,
        price:       v.price,
        extPartSku:  cmf.ext_part_sku || '',
        intPartSku:  cmf.int_part_sku || '',
        filamentExt: cmf.filament_ext || '',
        filamentInt: cmf.filament_int || '',
        // Real photo + PDP link for the "your build" summary cards.
        image:       v.image || '',
        night:       v.night || '',
        url:         v.url   || ''
      };

      // Track cheapest variant price per size — drives the "from €X"
      // hint on the size picker in the configurator.
      const sizeRec = sizeMap.get(sizeId);
      if (sizeRec && (!sizeRec.price || v.price < sizeRec.price)) {
        sizeRec.price = v.price;
      }
    }
  }

  const sizes    = Array.from(sizeMap.values()).sort((a, b) =>
    (SIZE_ORDER[a.id] || 99) - (SIZE_ORDER[b.id] || 99)
  );
  const shapes   = Array.from(shapeMap.values()).sort((a, b) => (a.sort || 0) - (b.sort || 0));
  const finishes = Array.from(finishMap.values());

  // ---- Connector ---------------------------------------------------
  const connectorProduct = parts.find(p => p.metafields?.role === 'connector');
  let connector = null;
  let connectorColors = [];
  if (connectorProduct) {
    const mf = connectorProduct.metafields || {};
    if (mf.model_file) {
      // Same per-key override mechanism as the modules — when the
      // connector product carries an uploaded GLB via the file_reference
      // metafield, the renderer's loadModel('connector') call resolves
      // to the Shopify CDN url instead of the bundled fallback.
      modelOverrides['connector'] = mf.model_file;
    }
    connector = {
      sku:        connectorProduct.handle.toUpperCase(),
      heightMm:   Number(mf.height_mm)   || 0,
      diameterMm: Number(mf.diameter_mm) || 0,
      weightG:    Number(mf.weight_g)    || 0,
      material:   mf.material || '',
      price:      connectorProduct.variants?.[0]?.price ?? 0
    };
    connectorColors = (connectorProduct.variants || []).map(v => {
      const cmf = v.metafields || {};
      return {
        id:          cmf.color_id || v.title.toLowerCase().replace(/\s+/g, '-'),
        name:        v.title,
        ext:         cmf.hex_ext || '#888',
        filament:    cmf.filament || '',
        filamentSku: cmf.filament_sku || '',
        variantId:   v.id,
        sku:         v.sku,
        // Real product photo (per colour variant) — build chips and the
        // detail modal prefer it over the flat hex swatch.
        image:       v.image || '',
        night:       v.night || '',
        url:         v.url   || ''
      };
    });
  }

  // ---- Light add-ons ----------------------------------------------
  // Sorted by `lase.sort` metafield (falls back to title) — same
  // ordering applied to other parts. Defined inline because we no
  // longer share a top-level helper in this adapter.
  const sortBySortMf = (a, b) => {
    const aS = Number(a.metafields?.sort) || 0;
    const bS = Number(b.metafields?.sort) || 0;
    if (aS !== bS) return aS - bS;
    return (a.title || '').localeCompare(b.title || '');
  };
  const lightProducts = parts.filter(p => p.metafields?.role === 'light').sort(sortBySortMf);
  const lightAddons   = [{ id: 'none', name: 'No light', sku: '', price: 0, ready: true, needsGlow: false }];
  for (const lp of lightProducts) {
    const mf = lp.metafields || {};
    const v  = lp.variants?.[0];
    lightAddons.push({
      id:        lp.handle,
      name:      lp.title,
      sku:       v?.sku || '',
      price:     v?.price ?? 0,
      ready:     mf.is_ready !== false,
      needsGlow: !!mf.needs_glow,
      material:  mf.material || '',
      variantId: v?.id
    });
  }

  // Connector colours are the SAME filaments as the matte modules
  // (white being the only extra) — force the visual hex to the matte
  // palette by colour id so swatches and 3D always match the modules,
  // regardless of what the connector variant metafields carry.
  const matteColors = (finishes.find(f => f.id === 'matte')?.colors) || [];
  connectorColors.forEach(c => {
    const m = matteColors.find(mc => mc.id === c.id);
    if (m) c.ext = m.ext || m.swatch || c.ext;
  });

  const partial = {};
  if (sizes.length)                    partial.sizes = sizes;
  if (shapes.length)                   partial.shapes = shapes;
  if (finishes.length)                 partial.finishes = finishes;
  if (connector)                       partial.connector = connector;
  if (connector)                       partial.connectorPrice = connector.price;
  if (connectorColors.length)          partial.connectorColors = connectorColors;
  if (lightAddons.length > 1)          partial.lightAddons = lightAddons;
  if (Object.keys(modelOverrides).length) partial.models = modelOverrides;
  // When Shopify provides at least one shape (e.g. P01), align the
  // default-spawn shape with it. Otherwise modules initialise with
  // a bundled placeholder id that no longer matches the catalogue
  // (e.g. 'lantern') and the renderer's shape lookup fails.
  if (shapes.length) partial.defaultShape = shapes[0].id;

  // ---- Vase insert (glass) ----------------------------------------
  // The "Vase" use-as toggle is backed by the REAL glass product
  // (lase.role = "vase"): its price rides the totals, its variant is
  // added to the cart, and the build chip shows its product photo —
  // one product, one source of truth, no phantom "vase" line.
  const vaseProduct = parts.find(p => p.metafields?.role === 'vase');
  if (vaseProduct && vaseProduct.variants && vaseProduct.variants.length) {
    const v0 = vaseProduct.variants[0];
    partial.useAsProducts = {
      vase: {
        variantId: v0.id,
        sku:       v0.sku || '',
        price:     v0.price || 0,
        image:     v0.image || '',
        url:       v0.url || '',
        title:     vaseProduct.title || 'Glass vase'
      }
    };
    partial.useAsPricing = { vase: v0.price || 0 };
  }
  return partial;
}

// Merge Shopify's partial catalogue OVER the bundled defaults using
// per-id replacement, NOT a top-level overwrite. This way a store
// that has uploaded only a few parts so far still sees the rest of
// the configurator working — bundled defaults fill the gaps.
//
//   • sizes     — replace bundled entry when its id appears in Shopify
//   • finishes  — same, plus colour lists merged per-id within finish
//   • connector — only swapped wholesale if Shopify provides one
//   • connectorColors / lightAddons — merged per-id
function mergeShopifyCatalogue (bundled, shopify) {
  const out = { ...bundled };

  const mergeById = (a, b) => {
    if (!Array.isArray(a)) return b;
    if (!Array.isArray(b)) return a;
    const map = new Map(a.map(x => [x.id, x]));
    b.forEach(x => map.set(x.id, x));
    return Array.from(map.values());
  };

  if (shopify.sizes) out.sizes = mergeById(bundled.sizes, shopify.sizes);

  // Shapes (patterns): REPLACE bundled when Shopify provides any.
  // The bundled set is just a dev placeholder; once admin has even
  // a single lase_pattern metaobject + sestava product, Shopify is
  // authoritative. Falling through to mergeById here would leave
  // the bundled 'lantern'/'cylinder'/'sphere' next to a Shopify
  // 'P01' in the picker — which is wrong.
  if (shopify.shapes && shopify.shapes.length) {
    out.shapes = shopify.shapes;
  }
  // defaultShape may be set by the adapter when shapes were replaced.
  if (shopify.defaultShape) out.defaultShape = shopify.defaultShape;

  if (shopify.finishes) {
    const finishMap = new Map((bundled.finishes || []).map(f => [f.id, f]));
    shopify.finishes.forEach(sf => {
      const bf = finishMap.get(sf.id);
      finishMap.set(sf.id, {
        ...bf,
        ...sf,
        // REPLACE bundled colours when Shopify has any for this
        // finish — once a finish is populated in admin, its
        // Shopify palette is authoritative. Bundled colours only
        // survive for finishes that have NO Shopify products yet
        // (e.g. Glow with no products → keep bundled Glow palette).
        colors: (sf.colors && sf.colors.length) ? sf.colors : (bf?.colors || []),
        // Carry the variantMap forward — `bundled` doesn't have one,
        // so falling through `...sf` already drops it onto the merged
        // record; this explicit assignment is defensive in case a
        // future bundled finish ever ships with one.
        variantMap: sf.variantMap || {}
      });
    });
    out.finishes = Array.from(finishMap.values());
  }

  if (shopify.connector) {
    out.connector = { ...bundled.connector, ...shopify.connector };
    out.connectorPrice = out.connector.price ?? bundled.connectorPrice;
  }
  if (shopify.connectorColors && shopify.connectorColors.length) {
    // Same logic as colours: when Shopify has a Konektor product,
    // its palette is authoritative — replace bundled connector
    // colours entirely instead of merging.
    out.connectorColors = shopify.connectorColors;
  }
  if (shopify.lightAddons) {
    out.lightAddons = mergeById(bundled.lightAddons, shopify.lightAddons);
  }
  // Models map (3D files) — per-key merge so a partial Shopify
  // upload (e.g. only module_M.glb uploaded) keeps the bundled
  // filenames for the others.
  if (shopify.models) {
    out.models = { ...(bundled.models || {}), ...shopify.models };
  }
  // Use-as add-ons backed by real products (vase = glass insert):
  // carry the product refs + pricing through so the build chip shows
  // the photo, the totals include the price, and the cart gets the
  // variant. Whitelisted like every other Shopify-authoritative key.
  if (shopify.useAsProducts) {
    out.useAsProducts = { ...(bundled.useAsProducts || {}), ...shopify.useAsProducts };
  }
  if (shopify.useAsPricing) {
    out.useAsPricing = { ...(bundled.useAsPricing || {}), ...shopify.useAsPricing };
  }
  return out;
}

function init () {
  document.querySelectorAll('[data-configurator]').forEach(root => {
    if (root.dataset.cfgBound) return;
    root.dataset.cfgBound = '1';

    let config = { ...DEFAULT_CONFIG };

    // Layer 1: inline assetBase / overrides from the section
    const inline = root.querySelector('[data-cfg-config]');
    if (inline) {
      try {
        const override = JSON.parse(inline.textContent);
        config = { ...config, ...override };
      } catch (e) {
        console.warn('cfg: inline config parse failed', e);
      }
    }

    // Layer 2: Shopify parts catalogue, if present. Merged per-id
    // over the bundled defaults — a Shopify entry overrides the
    // bundled one when their IDs match; bundled entries the store
    // hasn't created yet stay in place. Lets the store launch
    // incrementally (one part at a time) without breaking the UI.
    const partsScript = root.querySelector('[data-cfg-parts]');
    if (partsScript) {
      try {
        const parts   = JSON.parse(partsScript.textContent);
        const adapted = configFromShopifyParts(parts);
        if (adapted) config = mergeShopifyCatalogue(config, adapted);
      } catch (e) {
        console.warn('cfg: Shopify parts parse failed', e);
      }
    }

    config.assetBase = config.assetBase || '';
    new MonoConfigurator(root, config);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
document.addEventListener('shopify:section:load', init);
